import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, Star, Calendar, CreditCard, Lock, Users, Plus, X, User, Clock, Lightbulb, TrendingUp, RotateCcw, Mail, Shield, Search, MapPin } from 'lucide-react';
import { meetingService, userService } from '../utils/supabaseData';
import { MeetingStorage } from '../utils/meetingStorage';
import { PaymentUtils } from '../utils/stripeConfig';
import { StaffStorage, StaffUtils } from '../utils/staffStorage';
import { staffService } from '../utils/supabaseStaffService';
import { PhysicianPreferences } from '../utils/physicianPreferences';
import { ExternalProviderInvitation } from '../utils/externalProviderInvitation';
import { authService } from '../utils/supabase';
import { logger } from '../utils/logger';
import { adminImpersonationService } from '../utils/adminImpersonationService';
import emailService from '../utils/emailService';
import YelpRestaurantSearch from '../components/YelpRestaurantSearch';
import { UnifiedProviderSearch } from '../utils/unifiedProviderSearch';
import { preApprovedRepsService } from '../utils/preApprovedRepsService';
import restaurantFavoritesService from '../services/restaurantFavoritesService';
// Phase 3: Multi-physician office lunch components
import LocationSelector from '../components/LocationSelector';
import AddLocationModal from '../components/AddLocationModal';
import SmartCalendar from '../components/SmartCalendar';
import PhysicianSelector from '../components/PhysicianSelector';
import { attendeeService } from '../services/attendeeService';
import { customerLocationService } from '../services/locationService';
import referralService from '../services/referralService';

const BookMeetingView = ({ selectedCustomer, setCurrentView, currentUser, hasValidSubscription }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Helper function to calculate end time
  const calculateEndTime = (startTime, durationMinutes) => {
    // Convert 12-hour to 24-hour format first
    const time24 = convertTo24Hour(startTime);
    const [hours, minutes] = time24.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    startDate.setMinutes(startDate.getMinutes() + durationMinutes);
    return startDate.toTimeString().slice(0, 8); // Return HH:MM:SS format for Supabase
  };

  // Helper function to convert 12-hour to 24-hour format
  const convertTo24Hour = (time12h) => {
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');

    if (hours === '12') {
      hours = '00';
    }
    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
  };

  // Helper function to ensure date is in YYYY-MM-DD format without timezone issues
  const formatDateForSupabase = (dateStr) => {
    // If already in YYYY-MM-DD format, return as-is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }

    // Parse the date and format it properly to avoid timezone shifts
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  };
  const [savedMeeting, setSavedMeeting] = useState(null);
  const [step, setStep] = useState(1);
  // currentUser and hasValidSubscription are now passed as props
  const [bookingData, setBookingData] = useState({
    date: '',
    time: '',
    restaurant: null,
    orderItems: [],
    notes: '',
    selectedStaff: [],
    staffOrders: {},
    totalCost: 0,
    // Phase 3: Location-based multi-physician support
    location: null,              // Selected practice_locations record
    invitedPhysicians: [],       // Array of physician IDs to invite (besides organizer)
    // Keep for backward compatibility
    additionalPhysicians: [],
    primaryPhysician: null
  });
  
  const [availableStaff, setAvailableStaff] = useState([]);
  
  const [bookingInsights, setBookingInsights] = useState(null);
  const [reorderSuggestion, setReorderSuggestion] = useState(null);
  
  // External provider invitation states
  const [isExternalProvider, setIsExternalProvider] = useState(false);
  const [externalProviderEmail, setExternalProviderEmail] = useState('');
  const [emailValidationError, setEmailValidationError] = useState('');
  const [invitationSent, setInvitationSent] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  // Multi-physician management states
  const [showPhysicianSearch, setShowPhysicianSearch] = useState(false);
  const [physicianSearchQuery, setPhysicianSearchQuery] = useState('');
  const [searchedPhysicians, setSearchedPhysicians] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPhysicianData, setSelectedPhysicianData] = useState({}); // Map of physicianId -> physician object
  const [physiciansAtLocation, setPhysiciansAtLocation] = useState([]); // Physicians at selected location for quick selection
  const [loadingLocationPhysicians, setLoadingLocationPhysicians] = useState(false);

  // Phase 3: Location selection modal
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);

  // Restaurant search and management states
  const [showRestaurantSearch, setShowRestaurantSearch] = useState(false);
  const [restaurantSearchQuery, setRestaurantSearchQuery] = useState('');
  const [searchedRestaurants, setSearchedRestaurants] = useState([]);
  const [isSearchingRestaurants, setIsSearchingRestaurants] = useState(false);
  const [showAddRestaurant, setShowAddRestaurant] = useState(false);
  const [sortedRestaurants, setSortedRestaurants] = useState([]);

  // Calendar navigation state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Advanced conflict resolution state
  const [showConflictResolution, setShowConflictResolution] = useState(false);
  const [conflictSuggestions, setConflictSuggestions] = useState([]);

  // New: Time slot modal and customer data
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [customerAvailability, setCustomerAvailability] = useState(null);
  const [customerMeetings, setCustomerMeetings] = useState([]);
  const [customerFavoriteRestaurants, setCustomerFavoriteRestaurants] = useState([]);

  const [additionalRestaurants, setAdditionalRestaurants] = useState([]);
  const [newRestaurant, setNewRestaurant] = useState({
    name: '',
    cuisine: '',
    address: '',
    phone: '',
    avgPrice: '$10-15',
    deliveryTime: '30-45 min'
  });

  useEffect(() => {
    // currentUser and hasValidSubscription are now passed as props from App.js

    // Load available staff for the selected customer
    if (selectedCustomer) {
      // Check if this is an external provider (from NPI search, not registered)
      // Check multiple indicators: source, isRegistered flag, ID starting with 'npi_', or manual invite
      const isExternal = selectedCustomer.isRegistered === false ||
                         selectedCustomer.source === 'npi' ||
                         selectedCustomer.source === 'NPI Registry' ||
                         selectedCustomer.source === 'Manual Invite' ||
                         selectedCustomer.isManualInvite === true ||
                         (typeof selectedCustomer.id === 'string' && selectedCustomer.id.startsWith('npi_')) ||
                         (typeof selectedCustomer.id === 'string' && selectedCustomer.id.startsWith('manual_'));
      setIsExternalProvider(isExternal);

      // For manual invites, pre-populate the email field
      if (selectedCustomer.isManualInvite && selectedCustomer.email) {
        setExternalProviderEmail(selectedCustomer.email);
      }

      // Only load database data for registered providers (not external/NPI-only)
      if (!isExternal) {
        // Load customer availability and meetings
        loadCustomerAvailabilityAndMeetings();

        const loadStaff = async () => {
          try {
            // Try Supabase first (production approach)
            const supabaseStaff = await staffService.getStaffForPhysician(selectedCustomer.id);

            if (supabaseStaff && supabaseStaff.length > 0) {
              setAvailableStaff(supabaseStaff);
            } else {
              // Fallback to localStorage (legacy)
              const localStaff = StaffStorage.getStaffForPhysician(selectedCustomer.id);
              const activeLocalStaff = localStaff.filter(s => s.isActive);
              setAvailableStaff(activeLocalStaff);
            }
          } catch (error) {
            logger.error('Error loading staff, using localStorage fallback', { error: error.message });

            // Final fallback to localStorage
            const localStaff = StaffStorage.getStaffForPhysician(selectedCustomer.id);
            const activeLocalStaff = localStaff.filter(s => s.isActive);
            setAvailableStaff(activeLocalStaff);
          }
        };

        loadStaff();

        // Load booking insights for intelligent recommendations
        const insights = PhysicianPreferences.getBookingInsights(selectedCustomer.id, []);
        setBookingInsights(insights);

        // Load reorder suggestions
        const reorder = PhysicianPreferences.getReorderSuggestions(selectedCustomer.id);
        setReorderSuggestion(reorder);
      }

      // Set primary physician from selected customer
      setBookingData(prev => ({
        ...prev,
        primaryPhysician: {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          role: 'primary',
          specialty: selectedCustomer.specialty,
          confirmed: true,
          rsvpStatus: 'attending'
        },
        // Pre-fill date and time if provided (e.g., from availability quick schedule)
        date: selectedCustomer.suggestedDate || prev.date,
        time: selectedCustomer.preferredTime || prev.time,
        // Pre-fill location if provided (from specific availability slot)
        location: selectedCustomer.suggestedLocation || prev.location
      }));
    }
  }, [selectedCustomer]);

  // Auto-advance to step 3 if coming from Quick Schedule with pre-filled data
  useEffect(() => {
    // Only auto-advance once, when data is first loaded
    if (bookingData.date && bookingData.time && bookingData.location && step === 1) {
      // Check if this is from Quick Schedule (has suggestedDate from Quick Schedule)
      if (selectedCustomer?.suggestedDate && selectedCustomer?.suggestedLocation) {
        logger.debug('BookMeetingView: Auto-advancing to step 3 (Quick Schedule)', {
          date: bookingData.date,
          time: bookingData.time,
          location: bookingData.location?.practice_locations?.location_name
        });
        setStep(3); // Skip to attendees step
      }
    }
  }, [bookingData.date, bookingData.time, bookingData.location, selectedCustomer, step]);

  // Load physicians at the selected location for quick selection
  useEffect(() => {
    const loadPhysiciansAtLocation = async () => {
      if (!bookingData.location?.location_id || !bookingData.date) {
        setPhysiciansAtLocation([]);
        return;
      }

      setLoadingLocationPhysicians(true);
      try {
        // Get day of week from selected date
        const date = new Date(bookingData.date);
        const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayOfWeek = daysOfWeek[date.getDay()];

        // Fetch physicians available at this location on this day
        const physicians = await customerLocationService.getPhysiciansAvailableOnDay(
          bookingData.location.location_id,
          dayOfWeek
        );

        // Filter out the organizer (selected customer)
        const filteredPhysicians = physicians.filter(p =>
          p.customer_id !== selectedCustomer?.id
        );

        setPhysiciansAtLocation(filteredPhysicians);
        logger.debug('Loaded physicians at location:', {
          locationId: bookingData.location.location_id,
          dayOfWeek,
          count: filteredPhysicians.length
        });
      } catch (error) {
        logger.error('Error loading physicians at location:', error);
        setPhysiciansAtLocation([]);
      } finally {
        setLoadingLocationPhysicians(false);
      }
    };

    loadPhysiciansAtLocation();
  }, [bookingData.location, bookingData.date, selectedCustomer]);

  // Reload availability when location changes (to get location-specific blocked periods)
  useEffect(() => {
    logger.debug('Location useEffect triggered', {
      hasCustomerId: !!selectedCustomer?.id,
      locationId: bookingData.location?.location_id,
      isExternalProvider
    });
    if (selectedCustomer?.id && bookingData.location?.location_id && !isExternalProvider) {
      logger.info('Location changed, reloading availability with location-specific blocked periods', {
        customerId: selectedCustomer.id,
        locationId: bookingData.location.location_id
      });
      loadCustomerAvailabilityAndMeetings(bookingData.location.location_id);
    }
  }, [bookingData.location?.location_id, selectedCustomer?.id, isExternalProvider]);

  // Note: Restaurant sorting is now handled by the Yelp restaurant search component

  // Email validation helper
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle external provider email validation
  const handleEmailChange = (email) => {
    setExternalProviderEmail(email);
    setEmailValidationError('');

    if (email && !validateEmail(email)) {
      setEmailValidationError('Please enter a valid email address');
    }
  };

  // Toggle physician from location quick-select list
  const handleToggleLocationPhysician = (physicianData) => {
    const physicianId = physicianData.customer_id;

    // Build full physician object for display
    const physicianObject = {
      id: physicianId,
      userId: physicianId,
      name: `${physicianData.user_profiles.first_name} ${physicianData.user_profiles.last_name}`,
      email: physicianData.user_profiles.email,
      specialty: physicianData.user_profiles.specialty,
      isRegistered: true
    };

    if (bookingData.invitedPhysicians.includes(physicianId)) {
      // Remove physician
      setBookingData({
        ...bookingData,
        invitedPhysicians: bookingData.invitedPhysicians.filter(id => id !== physicianId)
      });

      // Remove from selectedPhysicianData
      setSelectedPhysicianData(prev => {
        const newData = { ...prev };
        delete newData[physicianId];
        return newData;
      });
    } else {
      // Add physician
      setBookingData({
        ...bookingData,
        invitedPhysicians: [...bookingData.invitedPhysicians, physicianId]
      });

      // Add to selectedPhysicianData
      setSelectedPhysicianData(prev => ({
        ...prev,
        [physicianId]: physicianObject
      }));
    }
  };

  // Search for additional physicians to add to the meeting
  const searchPhysicians = async (query) => {
    setIsSearching(true);

    try {
      // Use UnifiedProviderSearch to search registered users and NPI Registry
      const results = await UnifiedProviderSearch.search({
        name: query,
        location: '', // No location filter for additional physicians
        limit: 20
      });

      // Filter out primary physician and already added physicians
      const filteredResults = results.filter(physician => {
        if (physician.id === bookingData.primaryPhysician?.id) return false;
        if (bookingData.additionalPhysicians.some(p => p.id === physician.id)) return false;
        return true;
      });

      setSearchedPhysicians(filteredResults);
    } catch (error) {
      logger.error('Error searching physicians', { error: error.message });
      setSearchedPhysicians([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Add physician to the meeting
  const addPhysician = (physician) => {
    const newPhysician = {
      id: physician.id,
      name: physician.name,
      email: physician.email,
      role: 'attendee',
      specialty: physician.specialty,
      hospital: physician.hospital,
      confirmed: false,
      rsvpStatus: 'pending'
    };
    
    setBookingData(prev => ({
      ...prev,
      additionalPhysicians: [...prev.additionalPhysicians, newPhysician]
    }));
    
    // Clear search
    setPhysicianSearchQuery('');
    setSearchedPhysicians([]);
    setShowPhysicianSearch(false);
  };

  // Remove physician from the meeting
  const removePhysician = (physicianId) => {
    setBookingData(prev => ({
      ...prev,
      additionalPhysicians: prev.additionalPhysicians.filter(p => p.id !== physicianId)
    }));
  };

  // Get total physician count
  const getTotalPhysicians = () => {
    return 1 + (bookingData.invitedPhysicians?.length || 0); // 1 for organizer + invited physicians
  };

  // Hybrid weighted restaurant recommendation system
  // Scoring: 30pts (Favorite) + 50pts (Recency) + 15pts (Popularity) + 5pts (Quality) = 100pts
  const sortRestaurantsIntelligently = (restaurants) => {
    if (!selectedCustomer) return restaurants;

    // Get physician's meeting history for analysis
    const meetingHistory = MeetingStorage.getMeetingsForUser(selectedCustomer.id, 'customer');

    // Track restaurant order history with dates
    const restaurantHistory = {}; // { restaurantName: [date1, date2, ...] }

    meetingHistory.forEach(meeting => {
      if (meeting.restaurant && meeting.status === 'completed' && meeting.date) {
        const restaurantName = meeting.restaurant;
        if (!restaurantHistory[restaurantName]) {
          restaurantHistory[restaurantName] = [];
        }
        restaurantHistory[restaurantName].push(new Date(meeting.date));
      }
    });

    // Find the oldest order date across all restaurants (for recency calculation)
    const today = new Date();
    let maxDaysAgo = 1; // Avoid division by zero

    Object.values(restaurantHistory).forEach(dates => {
      dates.forEach(date => {
        const daysAgo = Math.floor((today - date) / (1000 * 60 * 60 * 24));
        if (daysAgo > maxDaysAgo) maxDaysAgo = daysAgo;
      });
    });

    // Calculate restaurant scores
    const scoredRestaurants = restaurants.map(restaurant => {
      let score = 0;
      const reasonsForRecommendation = [];

      // 1. FAVORITE BONUS (30 points)
      const isFavorite = selectedCustomer.favoriteRestaurants?.includes(restaurant.name);
      if (isFavorite) {
        score += 30;
        reasonsForRecommendation.push('⭐ Listed as favorite');
      }

      // 2. RECENCY SCORE (50 points) - Encourages variety
      const orderDates = restaurantHistory[restaurant.name] || [];
      const orderCount = orderDates.length;

      if (orderCount > 0) {
        // Find most recent order
        const mostRecentDate = new Date(Math.max(...orderDates.map(d => d.getTime())));
        const daysAgo = Math.floor((today - mostRecentDate) / (1000 * 60 * 60 * 24));

        // Recency score: older = higher score
        const recencyScore = (daysAgo / maxDaysAgo) * 50;
        score += recencyScore;

        if (daysAgo > 60) {
          reasonsForRecommendation.push(`Last ordered ${daysAgo} days ago`);
        } else if (daysAgo > 30) {
          reasonsForRecommendation.push(`Ordered ${daysAgo} days ago`);
        }
      } else {
        // Never ordered before - give max recency score
        score += 50;
        // Don't add "New option to try" - let other factors speak for themselves
      }

      // 3. POPULARITY SCORE (15 points) - Proven winners
      if (orderCount > 0) {
        const popularityScore = Math.min(orderCount / 10, 1.0) * 15;
        score += popularityScore;

        if (orderCount >= 5) {
          reasonsForRecommendation.push(`Ordered ${orderCount} times before`);
        } else if (orderCount > 1) {
          reasonsForRecommendation.push(`Visited ${orderCount} times`);
        }
      }

      // 4. QUALITY SCORE (5 points) - High standards
      const qualityScore = (restaurant.rating / 5.0) * 5;
      score += qualityScore;

      if (restaurant.rating >= 4.5) {
        reasonsForRecommendation.push(`⭐ Highly rated (${restaurant.rating})`);
      }

      return {
        ...restaurant,
        intelligenceScore: score,
        visitHistory: orderCount,
        lastOrderDate: orderCount > 0 ? new Date(Math.max(...orderDates.map(d => d.getTime()))) : null,
        isRecommended: score > 40, // Threshold for "recommended" badge
        isFavorite: isFavorite,
        reasonsForRecommendation: reasonsForRecommendation
      };
    });

    // Sort by intelligence score (highest first) and return top 5
    const sortedByScore = scoredRestaurants.sort((a, b) => b.intelligenceScore - a.intelligenceScore);

    // Remove duplicates and return top 5
    const uniqueRestaurants = [];
    const seenNames = new Set();

    for (const restaurant of sortedByScore) {
      if (!seenNames.has(restaurant.name) && uniqueRestaurants.length < 5) {
        uniqueRestaurants.push(restaurant);
        seenNames.add(restaurant.name);
      }
    }

    logger.debug('Restaurant recommendations generated', {
      count: uniqueRestaurants.length,
      restaurants: uniqueRestaurants.map(r => ({
        name: r.name,
        score: r.intelligenceScore.toFixed(1),
        favorite: r.isFavorite,
        lastOrdered: r.lastOrderDate?.toLocaleDateString() || 'Never',
        visits: r.visitHistory
      }))
    });

    return uniqueRestaurants;
  };

  // Handle Google Places restaurant selection
  const handleGoogleRestaurantSelect = (restaurant) => {
    // Convert Yelp/Google Places format to RepConnect format
    // Preserve all important fields for database storage
    const convertedRestaurant = {
      id: restaurant.id,
      name: restaurant.name,
      cuisine: restaurant.cuisineType || 'Restaurant',
      cuisineType: restaurant.cuisineType || 'Restaurant', // Keep original field
      avgPrice: restaurant.priceRange || '$$',
      priceRange: restaurant.priceRange || '$$', // Keep original field
      deliveryTime: restaurant.estimatedDeliveryTime || '30-45 min',
      address: restaurant.address,
      rating: restaurant.rating || 0,
      phone: restaurant.phone, // Preserve phone
      url: restaurant.url, // Preserve Yelp URL
      popularItems: restaurant.menu?.[0]?.items?.map(item => item.name) || null, // Only show if we have actual menu data
      source: restaurant.isYelpBusiness ? 'yelp' : (restaurant.isGooglePlace ? 'google_places' : 'fallback'),
      // Keep Yelp/Google-specific data for enhanced features
      googleData: restaurant.isGooglePlace ? {
        placeId: restaurant.googlePlaceId,
        phone: restaurant.phone,
        website: restaurant.website,
        photos: restaurant.photos || [],
        openingHours: restaurant.openingHours || [],
        location: restaurant.location
      } : null,
      yelpData: restaurant.isYelpBusiness ? {
        yelpId: restaurant.id,
        reviewCount: restaurant.reviewCount,
        categories: restaurant.categories,
        imageUrl: restaurant.imageUrl
      } : null
    };

    // Add to additional restaurants and select it
    setAdditionalRestaurants(prev => [convertedRestaurant, ...prev]);
    setBookingData(prev => ({...prev, restaurant: convertedRestaurant}));
    setShowRestaurantSearch(false);
  };

  // Search restaurants (simulated API call)
  const searchRestaurants = async (query) => {
    setIsSearchingRestaurants(true);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mock restaurant search results (in real app, this would be an API call)
    const mockSearchResults = [
      {
        id: `search_${Date.now()}_1`,
        name: `${query} Bistro`,
        cuisine: 'American',
        avgPrice: '$15-20',
        deliveryTime: '25-35 min',
        rating: 4.3,
        address: '123 Main Street',
        phone: '(555) 123-4567',
        popularItems: ['Classic Burger', 'Caesar Salad', 'Fish & Chips'],
        source: 'search'
      },
      {
        id: `search_${Date.now()}_2`,
        name: `The ${query} Kitchen`,
        cuisine: 'Mediterranean',
        avgPrice: '$12-18',
        deliveryTime: '20-30 min',
        rating: 4.1,
        address: '456 Oak Avenue',
        phone: '(555) 234-5678',
        popularItems: ['Mediterranean Bowl', 'Grilled Chicken', 'Hummus Plate'],
        source: 'search'
      }
    ];
    
    setSearchedRestaurants(mockSearchResults);
    setIsSearchingRestaurants(false);
  };
  
  // Add custom restaurant
  const addCustomRestaurant = () => {
    if (!newRestaurant.name.trim()) return;
    
    const customRestaurant = {
      id: `custom_${Date.now()}`,
      ...newRestaurant,
      rating: 0,
      popularItems: ['Custom menu items'],
      source: 'custom'
    };
    
    // Add to additional restaurants list
    setAdditionalRestaurants(prev => [customRestaurant, ...prev]);
    
    // Select the new restaurant
    setBookingData(prev => ({...prev, restaurant: customRestaurant}));
    
    // Reset form and close modal
    setNewRestaurant({
      name: '',
      cuisine: '',
      address: '',
      phone: '',
      avgPrice: '$10-15',
      deliveryTime: '30-45 min'
    });
    setShowAddRestaurant(false);
  };

  // Generate available time slots based on provider preferences and existing meetings
  const getAvailableSlots = () => {
    const preferredTime = selectedCustomer?.preferredTime;
    let baseSlots = [
      '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM'
    ];

    // Adjust slots based on provider preferences
    if (preferredTime?.toLowerCase().includes('early')) {
      baseSlots = ['11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM'];
    } else if (preferredTime?.toLowerCase().includes('late')) {
      baseSlots = ['12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM'];
    }

    // Filter out time slots that conflict with existing meetings
    if (bookingData.date && selectedCustomer) {
      const existingMeetings = MeetingStorage.getAllMeetings();
      const conflictingSlots = new Set();

      existingMeetings.forEach(meeting => {
        // Check if meeting is on the same date and with the same customer
        const meetingDate = meeting.meeting_date || meeting.date;
        if (meetingDate === bookingData.date &&
            (meeting.customer_id === selectedCustomer.id || meeting.customer === selectedCustomer.name)) {

          // Add the exact meeting time to conflicting slots
          if (meeting.meeting_time || meeting.time) {
            const meetingTime = meeting.meeting_time || meeting.time;
            conflictingSlots.add(meetingTime);
          }
        }
      });

      // Filter out conflicting slots
      return baseSlots.filter(slot => !conflictingSlots.has(slot));
    }

    return baseSlots;
  };

  const availableSlots = getAvailableSlots();
  
  // Get the next 14 days for date selection
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();

    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      // Include all days including weekends for healthcare meetings
      // Healthcare providers often meet on weekends for dinner meetings
      dates.push(date);
    }

    return dates;
  };

  const availableDates = getAvailableDates();

  // Load customer availability and meetings for enhanced calendar
  const loadCustomerAvailabilityAndMeetings = async (locationId = null) => {
    if (!selectedCustomer?.id) {
      logger.debug('No selectedCustomer.id');
      return;
    }

    logger.debug('Loading data for customer', {
      id: selectedCustomer.id,
      name: selectedCustomer.name,
      email: selectedCustomer.email,
      locationId
    });

    try {
      // Load customer availability settings
      const { customerDataService } = await import('../utils/supabaseData');

      // First get general availability
      let availability = await customerDataService.getAvailability(selectedCustomer.id);
      logger.debug('Loaded general availability for customer', { customerName: selectedCustomer.name, hasAvailability: !!availability });

      // If a location is selected, also get location-specific availability (which includes blocked periods)
      // This handles office_staff who set blocked periods for physicians
      if (locationId) {
        const locationAvailability = await customerDataService.getAvailabilityByLocation(selectedCustomer.id, locationId);
        logger.debug('Loaded location-specific availability', {
          locationId,
          hasLocationAvailability: !!locationAvailability,
          hasBlockedPeriods: !!locationAvailability?.blocked_periods?.length
        });

        if (locationAvailability) {
          // Merge location-specific data with general availability
          // Location-specific blocked_periods take precedence
          availability = {
            ...availability,
            // Use location-specific weekly availability if available
            weekly_availability: locationAvailability.weekly_availability || availability?.weekly_availability,
            // Merge blocked periods from both sources
            blocked_periods: [
              ...(availability?.blocked_periods || []),
              ...(locationAvailability.blocked_periods || [])
            ],
            meeting_preferences: locationAvailability.meeting_preferences || availability?.meeting_preferences
          };
          logger.debug('Merged availability with location-specific data', {
            totalBlockedPeriods: availability.blocked_periods?.length
          });
        }
      }

      if (!availability) {
        logger.warn('No availability data found in database for this customer', { customerId: selectedCustomer.id });
      }

      setCustomerAvailability(availability);

      // Load customer meetings to check for conflicts
      const meetings = await meetingService.getAllMeetings(selectedCustomer.id, 'customer');
      logger.debug('Loaded meetings', { meetingsCount: meetings?.length });
      setCustomerMeetings(meetings || []);

      // Load customer's favorite restaurants
      await loadCustomerFavorites();
    } catch (error) {
      logger.error('Error loading customer data', { error: error.message, customerId: selectedCustomer?.id });
      setCustomerAvailability(null);
      setCustomerMeetings([]);
    }
  };

  // Load customer's favorite restaurants with last booking dates
  const loadCustomerFavorites = async () => {
    if (!selectedCustomer?.id) return;

    try {
      // Get customer's favorite restaurants
      const favorites = await restaurantFavoritesService.getFavoriteRestaurants(selectedCustomer.id);

      if (!favorites || favorites.length === 0) {
        setCustomerFavoriteRestaurants([]);
        return;
      }

      // Get all completed meetings for this customer to find last booking dates
      const allMeetings = await meetingService.getAllMeetings(selectedCustomer.id, 'customer');
      const completedMeetings = allMeetings?.filter(m => m.status === 'completed') || [];

      // Enhance favorites with last booking date
      const favoritesWithLastBooking = favorites.map(fav => {
        const restaurant = fav.restaurant;

        // Find most recent completed meeting with this restaurant
        const meetingsWithThisRestaurant = completedMeetings.filter(m =>
          m.restaurant_name === restaurant.name ||
          m.restaurant_id === restaurant.id
        );

        const lastBooking = meetingsWithThisRestaurant.length > 0
          ? meetingsWithThisRestaurant.sort((a, b) =>
              new Date(b.meeting_date) - new Date(a.meeting_date)
            )[0]
          : null;

        return {
          ...restaurant,
          id: restaurant.id,
          name: restaurant.name,
          cuisine: restaurant.cuisine_type || 'Restaurant',
          avgPrice: restaurant.price_range || '$10-15',
          rating: restaurant.rating || 0,
          address: restaurant.address || '',
          deliveryTime: '30-45 min',
          isFavorite: true,
          lastBookedDate: lastBooking?.meeting_date || null,
          notes: fav.notes
        };
      });

      setCustomerFavoriteRestaurants(favoritesWithLastBooking);
    } catch (error) {
      logger.error('Error loading customer favorites', { error: error.message });
      setCustomerFavoriteRestaurants([]);
    }
  };

  // Process favorites for intelligent recommendations when favorites load
  useEffect(() => {
    if (customerFavoriteRestaurants.length > 0 && selectedCustomer) {
      const recommendations = sortRestaurantsIntelligently(customerFavoriteRestaurants);
      setSortedRestaurants(recommendations);
    } else {
      setSortedRestaurants([]);
    }
  }, [customerFavoriteRestaurants, selectedCustomer]);

  // Helper: Check if a time slot overlaps with a blocked period
  const isSlotBlockedByPeriod = (slotStart, slotEnd, period, dateString) => {
    // Check if this date falls within the blocked period's date range
    const checkDate = new Date(dateString);
    const periodStart = new Date(period.startDate);
    const periodEnd = new Date(period.endDate);

    // Date must be within the blocked period range
    if (checkDate < periodStart || checkDate > periodEnd) {
      return false;
    }

    // If it's an all-day block, the entire day is blocked
    if (period.allDay !== false) {
      return true;
    }

    // For time-specific blocks, check if the slot overlaps with the blocked time
    if (period.startTime && period.endTime) {
      // Convert times to comparable format (HH:MM)
      const blockStart = period.startTime;
      const blockEnd = period.endTime;

      // Check for overlap: slot overlaps if it starts before block ends AND ends after block starts
      return slotStart < blockEnd && slotEnd > blockStart;
    }

    // Default to blocked if we can't determine
    return true;
  };

  // Get day availability status for calendar coloring
  const getDayAvailabilityStatus = (dateString) => {
    // If customer availability hasn't loaded yet, treat as available
    if (!customerAvailability) {
      return 'available';
    }

    // Log blocked periods once for debugging (only for Dec 18)
    if (dateString === '2025-12-18' && customerAvailability?.blocked_periods?.length > 0) {
      logger.info('Checking blocked periods for Dec 18', {
        blockedPeriods: customerAvailability.blocked_periods
      });
    }

    const date = new Date(dateString + 'T12:00:00');
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];

    // Check for all-day blocks (vacation/out of office)
    const isFullDayBlocked = customerAvailability?.blocked_periods?.some(period => {
      const start = new Date(period.startDate);
      const end = new Date(period.endDate);
      const checkDate = new Date(dateString);
      // Only consider all-day blocks for full day blocking
      return checkDate >= start && checkDate <= end && period.allDay !== false;
    });
    if (isFullDayBlocked) return 'blocked';

    // Check weekly availability
    const daySlots = customerAvailability?.weekly_availability?.[dayOfWeek] || [];
    if (daySlots.length === 0) return 'unavailable';

    // Check for time-specific blocks that may partially block slots
    const blockedPeriods = customerAvailability?.blocked_periods || [];
    let availableSlotsCount = 0;
    let blockedSlotsCount = 0;

    daySlots.forEach(slot => {
      const isSlotBlocked = blockedPeriods.some(period =>
        isSlotBlockedByPeriod(slot.startTime, slot.endTime, period, dateString)
      );
      if (isSlotBlocked) {
        blockedSlotsCount++;
      } else {
        availableSlotsCount++;
      }
    });

    // If all slots are blocked by time-specific blocks, treat as blocked
    if (blockedSlotsCount > 0 && availableSlotsCount === 0) {
      return 'blocked';
    }

    // Check meetings for this day
    const dayMeetings = customerMeetings.filter(m => m.meeting_date === dateString);

    // Count confirmed and pending meetings
    const confirmedMeetings = dayMeetings.filter(m => m.status === 'confirmed');
    const pendingMeetings = dayMeetings.filter(m => m.status === 'pending');

    // Calculate total unavailable slots (blocked + booked)
    const totalUnavailable = blockedSlotsCount + confirmedMeetings.length;

    if (totalUnavailable >= daySlots.length) {
      return 'fully-booked'; // Red - all slots unavailable
    }
    if (totalUnavailable > 0 || confirmedMeetings.length > 0) {
      return 'partially-booked'; // Orange - some slots available
    }
    if (pendingMeetings.length > 0 && confirmedMeetings.length === 0) {
      return 'pending'; // Yellow - pending meetings
    }

    return 'available'; // Green/white - available
  };

  // Get available time slots for a specific day
  const getAvailableTimeSlotsForDay = (dateString) => {
    logger.debug('Getting slots for date', { dateString, hasAvailability: !!customerAvailability });
    if (!dateString || !customerAvailability) {
      logger.debug('No date or availability');
      return [];
    }

    const date = new Date(dateString + 'T12:00:00');
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
    const daySlots = customerAvailability.weekly_availability?.[dayOfWeek] || [];
    logger.debug('Availability slots for day', { dayOfWeek, slotsCount: daySlots.length });

    // Get meetings for this day
    const dayMeetings = customerMeetings.filter(m => m.meeting_date === dateString);
    logger.debug('Meetings on this day', { meetingsCount: dayMeetings.length });

    // Get blocked periods
    const blockedPeriods = customerAvailability?.blocked_periods || [];

    // Return whole availability slots (not 30-min intervals)
    // Each slot is selectable as one complete time block
    const availableSlots = [];
    daySlots.forEach(slot => {
      // Check if this entire slot is already booked
      // A slot is booked if any meeting starts at this slot's start time
      const isBooked = dayMeetings.some(m => {
        const meetingStart = m.start_time?.slice(0, 5); // Get HH:MM
        return meetingStart === slot.startTime;
      });

      // Check if this slot is blocked by any blocked period
      const isBlocked = blockedPeriods.some(period =>
        isSlotBlockedByPeriod(slot.startTime, slot.endTime, period, dateString)
      );

      if (!isBooked && !isBlocked) {
        const startTime12 = formatTimeTo12Hour(slot.startTime);
        const endTime12 = formatTimeTo12Hour(slot.endTime);

        availableSlots.push({
          time24: slot.startTime,
          time12: startTime12,
          endTime24: slot.endTime,
          endTime12: endTime12,
          label: slot.label || 'Available',
          timeRange: `${startTime12} - ${endTime12}`
        });
      } else if (isBlocked) {
        logger.debug('Slot blocked by blocked period', { slot: slot.startTime, dateString });
      }
    });

    logger.debug('Returning available slots', { availableSlotsCount: availableSlots.length });
    return availableSlots;
  };

  // Format 24-hour time to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Handle calendar day click
  const handleCalendarDayClick = (dateString) => {
    const status = getDayAvailabilityStatus(dateString);

    // Don't allow selecting blocked or unavailable days
    if (status === 'blocked' || status === 'unavailable') return;

    // Open time slot modal
    setSelectedCalendarDate(dateString);
    setShowTimeSlotModal(true);
  };

  // Handle time slot selection from modal
  const handleTimeSlotSelection = (timeSlot) => {
    setBookingData({
      ...bookingData,
      date: selectedCalendarDate,
      time: timeSlot.time12
    });
    setShowTimeSlotModal(false);
    setSelectedCalendarDate(null);
  };

  // Calendar helper function to generate calendar grid
  const getCalendarDays = (month) => {
    const year = month.getFullYear();
    const monthNum = month.getMonth();

    // Get first day of the month and how many days it has
    const firstDay = new Date(year, monthNum, 1);
    const lastDay = new Date(year, monthNum + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    // Create array for calendar grid (6 weeks × 7 days = 42 cells)
    const calendarDays = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(new Date(year, monthNum, day));
    }

    // Fill remaining cells to complete the grid (optional - makes calendar look complete)
    while (calendarDays.length < 42) {
      calendarDays.push(null);
    }

    return calendarDays;
  };

  // Helper functions for time slot grid
  const getNext7Days = () => {
    const days = [];

    // If a date is selected, show the week containing that date
    if (bookingData.date) {
      // IMPORTANT: Use local time, not UTC
      const selectedDate = new Date(bookingData.date + 'T12:00:00');

      // Find the start of the week (Sunday) for the selected date
      const startOfWeek = new Date(selectedDate);
      startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());

      // Generate 7 days starting from Sunday
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        days.push(date);
      }
    } else {
      // Default: show next 7 days starting tomorrow
      const today = new Date();
      for (let i = 1; i <= 7; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        days.push(date);
      }
    }

    return days;
  };

  const getTimeSlots = () => {
    const availability = getProviderAvailability();
    if (!availability) {
      // Fallback to default slots if no availability set
      return ['11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM'];
    }

    // Generate time slots based on provider's actual availability windows
    const timeSlots = new Set();

    // Get all days in the current week being displayed
    const weekDays = getNext7Days();

    weekDays.forEach(dateString => {
      // IMPORTANT: Use local time, not UTC
      const date = new Date(dateString + 'T12:00:00');
      const dayName = date.toLocaleDateString('en-US', {
        weekday: 'long'
      }).toLowerCase();

      const dayAvailability = availability[dayName];
      if (dayAvailability && dayAvailability.length > 0) {
        dayAvailability.forEach(window => {
          // Generate 30-minute slots within each availability window
          const startTime = window.startTime;
          const endTime = window.endTime;

          // Convert to minutes for easier calculation
          const startMinutes = timeToMinutes(startTime);
          const endMinutes = timeToMinutes(endTime);

          // Generate 30-minute intervals
          for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
            const timeSlot = minutesToTime12Hour(minutes);
            timeSlots.add(timeSlot);
          }
        });
      }
    });

    // Convert Set to Array and sort
    const sortedSlots = Array.from(timeSlots).sort((a, b) => {
      return timeToMinutes12Hour(a) - timeToMinutes12Hour(b);
    });

    return sortedSlots.length > 0 ? sortedSlots : ['11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM'];
  };

  // Helper function to convert time string (HH:MM) to minutes
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to convert minutes to 12-hour time string
  const minutesToTime12Hour = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  // Helper function to convert 12-hour time to minutes for sorting
  const timeToMinutes12Hour = (timeString) => {
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let totalMinutes = minutes;

    if (period === 'AM' && hours === 12) {
      totalMinutes += 0; // 12 AM = 0 hours
    } else if (period === 'AM') {
      totalMinutes += hours * 60;
    } else if (period === 'PM' && hours === 12) {
      totalMinutes += 12 * 60; // 12 PM = 12 hours
    } else {
      totalMinutes += (hours + 12) * 60;
    }

    return totalMinutes;
  };

  // Helper function to load provider availability windows
  const getProviderAvailability = () => {
    try {
      const savedAvailability = localStorage.getItem('weekly_availability');
      if (savedAvailability) {
        return JSON.parse(savedAvailability);
      }
    } catch (error) {
      logger.error('Error loading provider availability', { error: error.message });
    }
    return null;
  };

  // Helper function to check if a time slot falls within provider availability
  const isWithinAvailabilityWindow = (dateString, timeSlot) => {
    const availability = getProviderAvailability();
    if (!availability) return true; // If no availability set, allow all times

    // IMPORTANT: Use local time, not UTC
    const date = new Date(dateString + 'T12:00:00');
    const dayName = date.toLocaleDateString('en-US', {
      weekday: 'long'
    }).toLowerCase();
    const dayAvailability = availability[dayName];

    if (!dayAvailability || dayAvailability.length === 0) {
      return false; // No availability set for this day
    }

    // Convert time slot to 24-hour format for comparison
    const slotTime24 = convertTo24HourForComparison(timeSlot);

    // Check if the time slot falls within any available window
    return dayAvailability.some(window => {
      const startTime = window.startTime;
      const endTime = window.endTime;

      return slotTime24 >= startTime && slotTime24 <= endTime;
    });
  };

  // Helper function to convert 12-hour time to 24-hour for comparison
  const convertTo24HourForComparison = (time12h) => {
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');

    if (hours === '12') {
      hours = '00';
    }
    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  // Helper function to check if provider has any availability for a specific day
  const checkProviderAvailabilityForDay = (dateString) => {
    const availability = getProviderAvailability();
    if (!availability) return true; // If no availability set, allow all days (fallback)

    // IMPORTANT: Use local time, not UTC
    const date = new Date(dateString + 'T12:00:00');
    const dayName = date.toLocaleDateString('en-US', {
      weekday: 'long'
    }).toLowerCase();

    const dayAvailability = availability[dayName];

    // Return true if the provider has ANY availability windows for this day of the week
    return dayAvailability && dayAvailability.length > 0;
  };

  const checkTimeSlotConflict = (dateString, timeSlot) => {
    if (!selectedCustomer) return false;

    const existingMeetings = MeetingStorage.getAllMeetings();

    return existingMeetings.some(meeting => {
      const meetingDate = meeting.meeting_date || meeting.date;
      const meetingTime = meeting.meeting_time || meeting.time;

      return meetingDate === dateString &&
             meetingTime === timeSlot &&
             (meeting.customer_id === selectedCustomer.id || meeting.customer === selectedCustomer.name);
    });
  };

  const checkIfPreferredTime = (timeSlot) => {
    const preferredTime = selectedCustomer.preferredTime?.toLowerCase();

    if (preferredTime?.includes('early')) {
      return ['11:00 AM', '11:30 AM', '12:00 PM'].includes(timeSlot);
    } else if (preferredTime?.includes('late')) {
      return ['1:30 PM', '2:00 PM', '2:30 PM'].includes(timeSlot);
    }

    return false;
  };

  // Advanced conflict resolution functions
  const generateConflictSuggestions = (originalDate, originalTime) => {
    const suggestions = [];
    const availability = getProviderAvailability();

    // Suggestion 1: Alternative times on the same day
    const sameDay = getSameDayAlternatives(originalDate, originalTime);
    if (sameDay.length > 0) {
      suggestions.push({
        type: 'same_day',
        title: 'Same Day Alternative Times',
        description: `Other available times on ${formatDate(originalDate)}`,
        options: sameDay.slice(0, 3), // Top 3 alternatives
        priority: 1
      });
    }

    // Suggestion 2: Alternative days with same time
    const sameTimes = getSameTimeAlternatives(originalDate, originalTime);
    if (sameTimes.length > 0) {
      suggestions.push({
        type: 'same_time',
        title: 'Alternative Days at Same Time',
        description: `${originalTime} slots on different days`,
        options: sameTimes.slice(0, 3),
        priority: 2
      });
    }

    // Suggestion 3: Provider's preferred times
    const preferredTimes = getPreferredTimeAlternatives(originalDate);
    if (preferredTimes.length > 0) {
      suggestions.push({
        type: 'preferred',
        title: 'Provider\'s Preferred Times',
        description: `Times that match ${selectedCustomer.name}'s preferences`,
        options: preferredTimes.slice(0, 3),
        priority: 3
      });
    }

    // Suggestion 4: Next week same time
    const nextWeek = getNextWeekAlternative(originalDate, originalTime);
    if (nextWeek) {
      suggestions.push({
        type: 'next_week',
        title: 'Next Week Same Time',
        description: 'Move to the following week',
        options: [nextWeek],
        priority: 4
      });
    }

    return suggestions.sort((a, b) => a.priority - b.priority);
  };

  const getSameDayAlternatives = (dateString, excludeTime) => {
    const alternatives = [];
    const timeSlots = getTimeSlots();

    timeSlots.forEach(slot => {
      if (slot !== excludeTime &&
          !checkTimeSlotConflict(dateString, slot) &&
          isWithinAvailabilityWindow(dateString, slot)) {
        alternatives.push({
          date: dateString,
          time: slot,
          label: `${slot}`,
          isPreferred: checkIfPreferredTime(slot)
        });
      }
    });

    return alternatives;
  };

  const getSameTimeAlternatives = (originalDate, timeSlot) => {
    const alternatives = [];
    const next7Days = getNext7Days();

    next7Days.forEach(day => {
      // IMPORTANT: Use local date components, not UTC
      const year = day.getFullYear();
      const month = String(day.getMonth() + 1).padStart(2, '0');
      const dayNum = String(day.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${dayNum}`;

      if (dateString !== originalDate &&
          !checkTimeSlotConflict(dateString, timeSlot) &&
          isWithinAvailabilityWindow(dateString, timeSlot)) {
        alternatives.push({
          date: dateString,
          time: timeSlot,
          label: `${formatDate(dateString)} at ${timeSlot}`,
          isPreferred: checkIfPreferredTime(timeSlot)
        });
      }
    });

    return alternatives;
  };

  const getPreferredTimeAlternatives = (originalDate) => {
    const alternatives = [];
    const preferredTime = selectedCustomer.preferredTime?.toLowerCase();
    let preferredSlots = [];

    if (preferredTime?.includes('early')) {
      preferredSlots = ['11:00 AM', '11:30 AM', '12:00 PM'];
    } else if (preferredTime?.includes('late')) {
      preferredSlots = ['1:30 PM', '2:00 PM', '2:30 PM'];
    }

    const next7Days = getNext7Days();
    next7Days.forEach(day => {
      // IMPORTANT: Use local date components, not UTC
      const year = day.getFullYear();
      const month = String(day.getMonth() + 1).padStart(2, '0');
      const dayNum = String(day.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${dayNum}`;

      preferredSlots.forEach(slot => {
        if (!checkTimeSlotConflict(dateString, slot) &&
            isWithinAvailabilityWindow(dateString, slot)) {
          alternatives.push({
            date: dateString,
            time: slot,
            label: `${formatDate(dateString)} at ${slot}`,
            isPreferred: true
          });
        }
      });
    });

    return alternatives;
  };

  const getNextWeekAlternative = (originalDate, timeSlot) => {
    const originalDateObj = new Date(originalDate + 'T12:00:00');
    const nextWeekDate = new Date(originalDateObj);
    nextWeekDate.setDate(originalDateObj.getDate() + 7);

    // IMPORTANT: Use local date components, not UTC
    const year = nextWeekDate.getFullYear();
    const month = String(nextWeekDate.getMonth() + 1).padStart(2, '0');
    const dayNum = String(nextWeekDate.getDate()).padStart(2, '0');
    const nextWeekString = `${year}-${month}-${dayNum}`;

    if (!checkTimeSlotConflict(nextWeekString, timeSlot) &&
        isWithinAvailabilityWindow(nextWeekString, timeSlot)) {
      return {
        date: nextWeekString,
        time: timeSlot,
        label: `${formatDate(nextWeekString)} at ${timeSlot}`,
        isPreferred: checkIfPreferredTime(timeSlot)
      };
    }

    return null;
  };

  const formatDate = (dateString) => {
    // IMPORTANT: Use local time, not UTC
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleConflictSelection = (dateString, timeSlot) => {
    if (checkTimeSlotConflict(dateString, timeSlot) || !isWithinAvailabilityWindow(dateString, timeSlot)) {
      // Generate suggestions and show conflict resolution modal
      const suggestions = generateConflictSuggestions(dateString, timeSlot);
      setConflictSuggestions(suggestions);
      setShowConflictResolution(true);

      // Also store the original conflicted selection for reference
      setBookingData({
        ...bookingData,
        date: dateString,
        time: timeSlot,
        hasConflict: true
      });
    } else {
      // No conflict, proceed normally
      setBookingData({
        ...bookingData,
        date: dateString,
        time: timeSlot,
        hasConflict: false
      });
    }
  };

  const applySuggestion = (suggestion) => {
    setBookingData({
      ...bookingData,
      date: suggestion.date,
      time: suggestion.time,
      hasConflict: false
    });
    setShowConflictResolution(false);
    setSelectedSuggestion(null);
  };

  // Staff management functions
  const handleSelectAllStaff = () => {
    const allSelected = availableStaff.length > 0 &&
      availableStaff.every(staff => bookingData.selectedStaff.find(s => s.id === staff.id));

    if (allSelected) {
      // Deselect all staff
      setBookingData({
        ...bookingData,
        selectedStaff: [],
        staffOrders: {}
      });
    } else {
      // Select all staff
      const newStaffOrders = {};
      availableStaff.forEach(staff => {
        newStaffOrders[staff.id] = {
          items: [],
          individualCost: 0,
          specialRequests: ''
        };
      });
      setBookingData({
        ...bookingData,
        selectedStaff: [...availableStaff],
        staffOrders: newStaffOrders
      });
    }
  };

  const handleStaffSelection = (staff) => {
    const isSelected = bookingData.selectedStaff.find(s => s.id === staff.id);
    if (isSelected) {
      // Remove staff member
      setBookingData({
        ...bookingData,
        selectedStaff: bookingData.selectedStaff.filter(s => s.id !== staff.id),
        staffOrders: { ...bookingData.staffOrders, [staff.id]: undefined }
      });
    } else {
      // Add staff member
      setBookingData({
        ...bookingData,
        selectedStaff: [...bookingData.selectedStaff, staff],
        staffOrders: { 
          ...bookingData.staffOrders, 
          [staff.id]: { 
            items: [], 
            individualCost: 0, 
            specialRequests: '' 
          } 
        }
      });
    }
  };

  const updateStaffOrder = (staffId, orderData) => {
    setBookingData({
      ...bookingData,
      staffOrders: {
        ...bookingData.staffOrders,
        [staffId]: { ...bookingData.staffOrders[staffId], ...orderData }
      }
    });
  };

  const calculateTotalCost = () => {
    const physicianCost = bookingData.orderItems.reduce((sum, item) => sum + item.price, 0);
    const staffCost = Object.values(bookingData.staffOrders).reduce((sum, order) => sum + (order?.individualCost || 0), 0);
    return physicianCost + staffCost;
  };

  const handleQuickReorder = () => {
    if (reorderSuggestion) {
      // Note: Reorder functionality disabled while using Yelp integration
      // The restaurant search component will handle restaurant selection
      logger.debug('Reorder suggestion available but functionality temporarily disabled during Yelp integration');
    }
  };

  // Handle external provider invitation workflow
  const handleExternalProviderInvitation = async (currentUser) => {
    try {
      // Validate external provider email
      if (!externalProviderEmail || !validateEmail(externalProviderEmail)) {
        throw new Error('Valid email address is required for external providers');
      }

      // Create invitation data
      const invitationData = {
        provider: selectedCustomer,
        salesRep: currentUser,
        meetingDetails: {
          date: bookingData.date,
          time: bookingData.time,
          restaurant: bookingData.restaurant,
          notes: bookingData.notes
        },
        providerEmail: externalProviderEmail
      };

      // Create the invitation (now saves to Supabase database)
      const invitation = await ExternalProviderInvitation.createInvitation(invitationData);

      if (invitation) {
        // Send the invitation email
        logger.debug('Sending external provider invitation email', {
          to: invitation.provider_email,
          provider: invitation.provider_name
        });

        const emailResult = await emailService.sendExternalProviderInvite(invitation);

        if (!emailResult.success) {
          logger.error('Email sending failed but invitation was created in database', {
            error: emailResult.error,
            invitationId: invitation.id
          });
          // Note: We still proceed with showing success since invitation is stored in database
          // User can potentially resend or follow up manually
        } else {
          logger.debug('✅ External provider invitation email sent successfully', {
            messageId: emailResult.messageId,
            invitationId: invitation.id
          });
        }

        setInvitationSent(true);
        setSavedMeeting({
          ...invitation,
          isExternalInvitation: true,
          customer: selectedCustomer.name,
          date: bookingData.date,
          time: bookingData.time,
          restaurant: bookingData.restaurant?.name,
          emailSent: emailResult.success,
          emailMessageId: emailResult.messageId
        });
        setSubmitSuccess(true);
      } else {
        throw new Error('Failed to create invitation in database');
      }

    } catch (error) {
      logger.error('Error creating external provider invitation', { error: error.message });
      alert(`Failed to send invitation: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmBooking = async () => {
    setIsSubmitting(true);

    // Declare effectiveUser at function scope so it's accessible in catch block
    // Always use currentUser prop - it's already set correctly by App.js
    let effectiveUser = currentUser;

    try {
      // Log if admin is impersonating a user
      const impersonationState = adminImpersonationService.getImpersonationState();
      if (impersonationState) {
        logger.debug('Admin impersonating user for meeting creation', {
          adminId: impersonationState.adminUserId,
          targetUserId: impersonationState.targetUserId,
          effectiveUserId: effectiveUser?.id
        });
      }

      if (!effectiveUser) {
        throw new Error('No current user found');
      }

      // Handle external provider invitations differently
      if (isExternalProvider) {
        return await handleExternalProviderInvitation(effectiveUser);
      }

      // Check if the sales rep is pre-approved by this customer
      let isPreApproved = false;
      try {
        isPreApproved = await preApprovedRepsService.isRepPreApproved(
          selectedCustomer.id,
          effectiveUser.id
        );
        logger.debug('Pre-approval check', {
          customerId: selectedCustomer.id,
          salesRepId: effectiveUser.id,
          isPreApproved
        });
      } catch (error) {
        logger.warn('Could not check pre-approval status', { error: error.message });
        isPreApproved = false;
      }

      const totalCost = calculateTotalCost();
      const physicianCost = bookingData.orderItems.reduce((sum, item) => sum + (parseFloat(item.price) * (item.quantity || 1)), 0);

      const meetingData = {
        customerId: selectedCustomer.id,
        customer: selectedCustomer.name,
        customerEmail: selectedCustomer.email,
        customerPhone: selectedCustomer.phone,
        salesRepId: effectiveUser.id.toString(), // Ensure string format
        salesRepName: effectiveUser.fullName || effectiveUser.name || `${effectiveUser.first_name || ''} ${effectiveUser.last_name || ''}`.trim(),
        salesRepEmail: effectiveUser.email,
        date: bookingData.date,
        time: bookingData.time,
        restaurant: bookingData.restaurant, // Keep full restaurant object
        restaurantId: bookingData.restaurant?.id || null,
        restaurantAddress: bookingData.restaurant?.address || '',
        orderItems: bookingData.orderItems,
        orderTotal: physicianCost.toFixed(2),
        notes: bookingData.notes,
        followUp: '',
        status: isPreApproved ? 'approved' : 'pending', // Auto-approve if pre-approved
        duration: 60, // Default 60 minutes
        createdBy: 'sales',
        // Staff-related data
        selectedStaff: bookingData.selectedStaff, // Add the actual staff array
        hasStaff: bookingData.selectedStaff.length > 0,
        staffCount: bookingData.selectedStaff.length,
        totalAttendees: bookingData.selectedStaff.length + getTotalPhysicians(), // Include all physicians
        totalCost: totalCost.toFixed(2),
        staffCost: (totalCost - physicianCost).toFixed(2),
        // Multi-physician support
        physicians: [
          bookingData.primaryPhysician,
          ...bookingData.additionalPhysicians
        ],
        totalPhysicians: getTotalPhysicians(),
        confirmedPhysicians: 1, // Primary is always confirmed
        pendingPhysicians: bookingData.additionalPhysicians.length
      };

      // Try Supabase first, fallback to localStorage
      let newMeeting;
      try {
        // First, ensure the customer exists in user_profiles table

        const existingCustomer = await userService.getUserById(selectedCustomer.id);

        if (!existingCustomer) {

          // Create customer record if it doesn't exist
          const customerData = {
            id: selectedCustomer.id,
            first_name: selectedCustomer.name.split(' ')[0] || 'Customer',
            last_name: selectedCustomer.name.split(' ').slice(1).join(' ') || '',
            email: selectedCustomer.email || `customer-${selectedCustomer.id}@placeholder.com`,
            phone: selectedCustomer.phone || '',
            company: selectedCustomer.company || selectedCustomer.organization || '',
            user_type: 'customer',
            specialty: selectedCustomer.specialty || '',
            created_at: new Date().toISOString()
          };

          try {
            const newCustomer = await userService.createUser(customerData);

          } catch (createError) {
            logger.warn('BookMeetingView: Could not create customer record', { error: createError.message });
          }
        } else {

        }

        const initialStatus = isPreApproved ? 'confirmed' : 'pending';

        // Transform data for Supabase schema with proper formatting
        const supabaseMeetingData = {
          customer_id: selectedCustomer.id, // Reference to customer in user_profiles
          sales_rep_id: effectiveUser.id,
          meeting_date: formatDateForSupabase(meetingData.date), // Proper date format
          start_time: convertTo24Hour(meetingData.time), // Convert to 24-hour format
          end_time: calculateEndTime(meetingData.time, 60), // Default 60 min
          status: initialStatus,
          // Phase 3: Location data
          location_id: bookingData.location?.location_id || null,
          location_name: bookingData.location?.practice_locations?.location_name || null,
          location_address: bookingData.location?.practice_locations?.address || null,
          restaurant_name: meetingData.restaurant?.name,
          restaurant_address: meetingData.restaurant?.address,
          restaurant_phone: meetingData.restaurant?.phone,
          restaurant_cuisine_type: meetingData.restaurant?.cuisineType,
          restaurant_price_range: meetingData.restaurant?.priceRange,
          restaurant_rating: meetingData.restaurant?.rating,
          restaurant_url: meetingData.restaurant?.url,
          restaurant_yelp_id: meetingData.restaurant?.id,
          estimated_cost: parseFloat(meetingData.totalCost || 0),
          attendee_count: meetingData.totalAttendees + 1, // Total physicians + staff + 1 for sales rep
          selected_staff_ids: meetingData.selectedStaff?.map(staff => staff.id) || [],
          booking_notes: meetingData.notes, // Booking notes (catering instructions, dietary restrictions)
          meeting_type: 'lunch'
        };

        // Phase 3: Build attendee list for multi-physician office lunches
        const attendees = [
          // Organizer (primary physician)
          {
            user_id: selectedCustomer.id,
            attendee_type: 'physician',
            is_organizer: true,
            rsvp_status: isPreApproved ? 'accepted' : 'pending'  // Auto-accept if pre-approved
          },
          // Additional invited physicians (Phase 3)
          ...bookingData.invitedPhysicians.map(physicianId => ({
            user_id: physicianId,
            attendee_type: 'physician',
            is_organizer: false,
            rsvp_status: 'pending'
          })),
          // Sales rep (always auto-accepted)
          {
            user_id: effectiveUser.id,
            attendee_type: 'sales_rep',
            is_organizer: false,
            rsvp_status: 'accepted'
          }
        ];

        // Phase 3: Create meeting with attendees (replaces old createMeeting)
        newMeeting = await meetingService.createMeetingWithAttendees(supabaseMeetingData, attendees);

        // Send email notification to the primary physician (organizer)
        try {
          const emailResult = await emailService.sendMeetingBookingNotification({
            physicianName: selectedCustomer.name,
            physicianEmail: selectedCustomer.email,
            salesRepName: effectiveUser.fullName || effectiveUser.name || `${effectiveUser.first_name || ''} ${effectiveUser.last_name || ''}`.trim(),
            meetingDate: bookingData.date,
            meetingTime: bookingData.time,
            locationName: bookingData.location?.practice_locations?.location_name || 'Location not specified',
            locationAddress: bookingData.location?.practice_locations?.address || '',
            restaurantName: bookingData.restaurant?.name || 'Restaurant not specified',
            notes: bookingData.notes || '',
            totalAttendees: meetingData.totalAttendees + 1 // Physicians + staff + sales rep
          });

          if (emailResult.success) {
            logger.info('Meeting notification email sent successfully', {
              meetingId: newMeeting.id,
              physicianEmail: selectedCustomer.email
            });
          } else {
            logger.warn('Failed to send meeting notification email', {
              error: emailResult.error,
              physicianEmail: selectedCustomer.email
            });
          }
        } catch (emailError) {
          // Log but don't fail the booking if email fails
          logger.error('Error sending meeting notification email', {
            error: emailError.message,
            physicianEmail: selectedCustomer.email
          });
        }

        // Send invitation emails to invited physicians
        if (bookingData.invitedPhysicians && bookingData.invitedPhysicians.length > 0) {
          logger.info('Sending invitation emails to invited physicians', {
            count: bookingData.invitedPhysicians.length
          });

          for (const physicianId of bookingData.invitedPhysicians) {
            // Get physician details from selectedPhysicianData
            let physician = selectedPhysicianData[physicianId];

            // Check if physician email is a placeholder (not real)
            const hasPlaceholderEmail = physician?.email &&
              (physician.email.includes('@pending.meetwithreps.com') ||
               physician.email.includes('placeholder'));

            // If physician data is missing, doesn't have email, or has placeholder email, fetch from database
            if (!physician || !physician.email || hasPlaceholderEmail) {
              logger.debug('Fetching physician details from database', { physicianId });
              try {
                const { supabase } = await import('../utils/supabase');
                const { data: physicianData, error } = await supabase
                  .from('user_profiles')
                  .select('id, first_name, last_name, email, specialty, npi_number, user_type, account_status')
                  .eq('id', physicianId)
                  .single();

                if (physicianData && !error) {
                  physician = {
                    id: physicianData.id,
                    name: `${physicianData.first_name || ''} ${physicianData.last_name || ''}`.trim(),
                    email: physicianData.email,
                    specialty: physicianData.specialty,
                    npi_number: physicianData.npi_number,
                    account_status: physicianData.account_status
                  };
                  logger.debug('Physician data fetched from database', { physician });
                }
              } catch (dbError) {
                logger.warn('Could not fetch physician from database', {
                  physicianId,
                  error: dbError.message
                });
              }
            }

            // Determine email recipient (use admin if physician has placeholder email)
            let emailRecipient = null;
            let emailRecipientName = null;

            // Check if physician email is valid (not placeholder)
            const physicianHasValidEmail = physician?.email &&
              !physician.email.includes('@pending.meetwithreps.com') &&
              !physician.email.includes('placeholder');

            if (physicianHasValidEmail) {
              emailRecipient = physician.email;
              emailRecipientName = physician.name;
              logger.debug('Using physician email for invitation', {
                physicianId,
                physicianEmail: emailRecipient
              });
            }

            // If no valid email, check for admin users (office staff managing this physician)
            if (!emailRecipient && physicianId) {
              logger.debug('No email for physician, checking for admin users', { physicianId });
              try {
                const { supabase } = await import('../utils/supabase');
                const { data: adminUsers, error } = await supabase
                  .from('admin_users')
                  .select(`
                    staff_id,
                    user_profiles:staff_id (
                      id,
                      first_name,
                      last_name,
                      email
                    )
                  `)
                  .eq('customer_id', physicianId)
                  .limit(1);

                if (adminUsers && adminUsers.length > 0 && !error) {
                  const admin = adminUsers[0].user_profiles;
                  if (admin && admin.email) {
                    emailRecipient = admin.email;
                    emailRecipientName = `${admin.first_name || ''} ${admin.last_name || ''}`.trim();
                    logger.info('Sending invitation to office staff admin for physician', {
                      physicianId,
                      adminEmail: emailRecipient,
                      adminName: emailRecipientName
                    });
                  }
                }
              } catch (adminError) {
                logger.warn('Could not check for admin users', {
                  physicianId,
                  error: adminError.message
                });
              }
            }

            // Skip placeholder emails (not real email addresses)
            if (!emailRecipient || emailRecipient.includes('@pending.meetwithreps.com') || emailRecipient.includes('placeholder')) {
              logger.warn('Skipping invitation email - no valid email found for physician or their admin', {
                physicianId,
                emailRecipient
              });
              continue;
            }

            try {
              const invitationResult = await emailService.sendPhysicianInvitation({
                physicianName: physician?.name || emailRecipientName,
                physicianEmail: emailRecipient,
                organizerName: selectedCustomer.name,
                salesRepName: effectiveUser.fullName || effectiveUser.name || `${effectiveUser.first_name || ''} ${effectiveUser.last_name || ''}`.trim(),
                meetingDate: bookingData.date,
                meetingTime: bookingData.time,
                locationName: bookingData.location?.practice_locations?.location_name || 'Location not specified',
                locationAddress: bookingData.location?.practice_locations?.address || '',
                restaurantName: bookingData.restaurant?.name || 'Restaurant not specified',
                totalAttendees: meetingData.totalAttendees + 1, // Physicians + staff + sales rep
                meetingId: newMeeting.id
              });

              if (invitationResult.success) {
                logger.info('Invitation email sent successfully', {
                  meetingId: newMeeting.id,
                  physicianId,
                  recipientEmail: emailRecipient,
                  recipientName: emailRecipientName,
                  sentToAdmin: emailRecipient !== physician?.email
                });
              } else {
                logger.warn('Failed to send invitation email', {
                  error: invitationResult.error,
                  physicianId,
                  recipientEmail: emailRecipient
                });
              }
            } catch (invitationError) {
              // Log but don't fail the booking if email fails
              logger.error('Error sending invitation email', {
                error: invitationError.message,
                physicianId,
                physicianEmail: physician.email
              });
            }
          }
        }

      } catch (error) {

        newMeeting = MeetingStorage.createMeetingRequest(meetingData);

      }

      if (newMeeting) {
        // Save staff orders if any staff were selected
        if (bookingData.selectedStaff.length > 0) {
          const staffOrdersData = bookingData.selectedStaff.map(staff => ({
            staffId: staff.id,
            staffName: staff.name,
            role: staff.role,
            items: bookingData.staffOrders[staff.id]?.items || [],
            individualCost: bookingData.staffOrders[staff.id]?.individualCost || 0,
            specialRequests: bookingData.staffOrders[staff.id]?.specialRequests || '',
            meetingId: newMeeting.id
          }));
          
          StaffStorage.recordStaffOrders(newMeeting.id, staffOrdersData);
        }
        
        setSavedMeeting(newMeeting);
        setSubmitSuccess(true);
      } else {
        throw new Error('Failed to save meeting');
      }
    } catch (error) {
      logger.error('Error booking meeting', {
        error: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });

      // Check if meeting was actually saved despite the error
      const meetings = MeetingStorage.getAllMeetings();

      if (meetings.length > 0) {
        const lastMeeting = meetings[meetings.length - 1];

        if (lastMeeting.salesRepId === effectiveUser.id.toString()) {

          setSavedMeeting(lastMeeting);
          setSubmitSuccess(true);
        } else {

          alert('Failed to schedule meeting. Please try again.');
        }
      } else {

        alert('Failed to schedule meeting. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadCalendar = () => {
    if (savedMeeting) {
      MeetingStorage.downloadICS(savedMeeting);
    }
  };

  if (!selectedCustomer) return null;

  // Success screen
  if (submitSuccess && savedMeeting) {
    return (
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {savedMeeting.isExternalInvitation ? 'Invitation Sent!' : 'Meeting Request Sent!'}
            </h2>
            <p className="text-gray-600 mb-2">
              {savedMeeting.isExternalInvitation ? 
                `An email invitation has been sent to ${selectedCustomer.name} at ${externalProviderEmail}` :
                `Your meeting request has been sent to ${selectedCustomer.name} for approval`
              }
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {savedMeeting.isExternalInvitation ? 
                `${selectedCustomer.name} will receive a professional email with meeting details and can confirm or decline with one click. You'll be notified immediately of their response.` :
                `${selectedCustomer.name} will receive a notification and can approve or decline your meeting request. You'll be notified of their decision.`
              }
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left max-w-md mx-auto">
              <h3 className="font-semibold text-gray-800 mb-3">Meeting Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">
                    {(() => {
                      // Handle both Supabase format (meeting_date) and localStorage format (date)
                      const dateStr = savedMeeting.meeting_date || savedMeeting.date || bookingData.date;
                      if (dateStr) {
                        // If it's already in YYYY-MM-DD format, parse it carefully to avoid timezone issues
                        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          return new Intl.DateTimeFormat('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }).format(new Date(dateStr + 'T12:00:00'));
                        }
                        return new Intl.DateTimeFormat('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }).format(new Date(dateStr));
                      }
                      return 'Date not available';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-medium">
                    {(() => {
                      // Handle both Supabase format (start_time) and localStorage format (time)
                      const timeStr = savedMeeting.start_time || savedMeeting.time || bookingData.time;
                      if (timeStr) {
                        // If it's in 24-hour format, convert back to 12-hour for display
                        if (timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
                          const [hours, minutes] = timeStr.split(':');
                          const hour12 = parseInt(hours) === 0 ? 12 : parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours);
                          const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                          return `${hour12}:${minutes} ${ampm}`;
                        }
                        return timeStr;
                      }
                      return 'Time not available';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Provider:</span>
                  <span className="font-medium">
                    {typeof savedMeeting.customer === 'object'
                      ? `${savedMeeting.customer.first_name || ''} ${savedMeeting.customer.last_name || ''}`.trim() || savedMeeting.customer.name
                      : savedMeeting.customer || selectedCustomer.name || 'Provider not available'}
                  </span>
                </div>
                {savedMeeting.restaurant && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Restaurant:</span>
                    <span className="font-medium">{savedMeeting.restaurant}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    savedMeeting.isExternalInvitation ? 
                      'bg-blue-100 text-blue-800' : 
                      'bg-yellow-100 text-yellow-800'
                  }`}>
                    {savedMeeting.isExternalInvitation ? 'Email Invitation Sent' : 'Awaiting Provider Approval'}
                  </span>
                </div>
                
                {/* Additional info for external invitations */}
                {savedMeeting.isExternalInvitation && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Invitation Email:</span>
                    <span className="font-medium text-blue-600">{externalProviderEmail}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleDownloadCalendar}
                className="flex items-center justify-center space-x-2 w-full max-w-xs mx-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                <Calendar className="w-4 h-4" />
                <span>Add to Calendar</span>
              </button>
              
              <button
                onClick={() => setCurrentView('meetings')}
                className="flex items-center justify-center space-x-2 w-full max-w-xs mx-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <span>View All Meetings</span>
              </button>
              
              <button
                onClick={() => setCurrentView('find')}
                className="text-blue-600 hover:underline text-sm"
              >
                Schedule Another Meeting
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check subscription status for payment-required users
  // Debug logging
  logger.debug('🔍 BookMeetingView subscription check:', {
    currentUser,
    userType: currentUser?.userType,
    email: currentUser?.email,
    hasValidSubscription,
    paymentRequired: currentUser ? PaymentUtils.isPaymentRequired(currentUser.userType) : 'no user'
  });

  // Demo/Development user exceptions - allow access for testing
  const isDemoUser = currentUser?.email === 'rep@repconnect.com' ||
                     (currentUser?.profile && currentUser.profile.email === 'rep@repconnect.com') ||
                     (currentUser?.user && currentUser.user.email === 'rep@repconnect.com') ||
                     currentUser?.email?.includes('rep') || // More flexible demo check
                     hasValidSubscription === true; // If hasValidSubscription is true, allow access

  // If hasValidSubscription is true, skip paywall entirely
  if (currentUser && PaymentUtils.isPaymentRequired(currentUser.userType) && !hasValidSubscription && !isDemoUser) {
    return (
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-800">Subscription Required</h2>
            <p className="text-gray-600 mt-1">Schedule Meeting with {selectedCustomer.name}</p>
          </div>
          
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-orange-600" />
            </div>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">
              Active Subscription Required
            </h3>
            
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              To schedule meetings with healthcare providers, you need an active RepConnect subscription. 
              Choose your plan to unlock all features and start booking meetings.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <div className="flex items-center space-x-3">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <div className="text-left">
                  <p className="font-medium text-blue-800">
                    {PaymentUtils.getPlanForUserType(currentUser.userType)?.name} Plan
                  </p>
                  <p className="text-blue-600 text-sm">
                    {PaymentUtils.formatCurrency(PaymentUtils.getPlanForUserType(currentUser.userType)?.price || 0)}/month
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => setCurrentView('settings')}
                className="w-full max-w-sm bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <CreditCard className="w-5 h-5" />
                <span>Subscribe Now</span>
              </button>
              
              <button
                onClick={() => setCurrentView('dashboard')}
                className="w-full max-w-sm border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
            
            <div className="mt-8 p-4 bg-gray-50 rounded-lg max-w-md mx-auto">
              <p className="text-sm text-gray-600 mb-2">
                <strong>What you'll get with your subscription:</strong>
              </p>
              <ul className="text-sm text-gray-600 space-y-1">
                {PaymentUtils.getPlanForUserType(currentUser.userType)?.features.slice(0, 4).map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md flex flex-col" style={{minHeight: '600px', maxHeight: '90vh'}}>
        <div className="p-6 border-b flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-800">Schedule Meeting with {selectedCustomer.name}</h2>
          <p className="text-gray-600 mt-1">{selectedCustomer.specialty} at {selectedCustomer.hospital}</p>
        </div>

        {/* Progress Steps - Mobile: Compact, Desktop: Full */}

        {/* Mobile: Compact version */}
        <div className="md:hidden px-6 py-3 border-b flex-shrink-0 bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Step {step}/{isExternalProvider ? 2 : 6}: {
                (isExternalProvider
                  ? ['Invite', 'Sent']
                  : ['Location', 'Date & Time', 'Attendees', 'Restaurant', 'Notes', 'Confirm']
                )[step - 1]
              }
            </span>
            {isExternalProvider && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Invitation
              </span>
            )}
          </div>
        </div>

        {/* Desktop: Full version with circles */}
        <div className="hidden md:block p-6 border-b flex-shrink-0">
          {isExternalProvider && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg flex items-center space-x-2">
              <Mail className="w-5 h-5" />
              <div>
                <p className="text-sm font-medium">Invite Provider to RepConnect</p>
                <p className="text-xs">This provider isn't on RepConnect yet. Send them an invitation to join.</p>
              </div>
            </div>
          )}

          <div className="flex items-start justify-between">
            {(isExternalProvider
              ? [{num: 1, label: 'Invite'}, {num: 2, label: 'Sent'}]
              : [{num: 1, label: 'Location'}, {num: 2, label: 'Date & Time'}, {num: 3, label: 'Attendees'}, {num: 4, label: 'Restaurant'}, {num: 5, label: 'Notes'}, {num: 6, label: 'Confirm'}]
            ).map((stepItem, index, array) => (
              <React.Fragment key={stepItem.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step > stepItem.num ? 'bg-blue-600 text-white' : step === stepItem.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step > stepItem.num ? <CheckCircle className="w-6 h-6" /> : stepItem.num}
                  </div>
                  <span className="text-sm mt-2 text-center">{stepItem.label}</span>
                </div>
                {index < array.length - 1 && (
                  <div className={`h-1 mx-2 self-start mt-5 ${
                    step > stepItem.num ? 'bg-blue-600' : 'bg-gray-200'
                  }`} style={{width: isExternalProvider ? '50px' : '65px', flexShrink: 0}} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Phase 3: Step 1 - Location Selection OR External Provider Email */}
          {step === 1 && (
            <div>
              {isExternalProvider ? (
                /* External Provider: Collect email for invitation */
                <div className="max-w-lg mx-auto">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Invite {selectedCustomer?.name}</h2>
                    <p className="text-gray-600">
                      {selectedCustomer?.isManualInvite
                        ? 'Verify the email address below and send them an invitation to join RepConnect.'
                        : "This provider isn't on RepConnect yet. Enter their email address to send them an invitation."
                      }
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                    {/* Provider Info */}
                    <div className="mb-6 pb-6 border-b border-gray-200">
                      <h3 className="text-sm font-medium text-gray-500 mb-3">Provider Information</h3>
                      <div className="space-y-2">
                        <p className="font-semibold text-gray-900">{selectedCustomer?.name}</p>
                        {selectedCustomer?.specialty && (
                          <p className="text-sm text-gray-600">{selectedCustomer.specialty}</p>
                        )}
                        {selectedCustomer?.hospital && (
                          <p className="text-sm text-gray-600">{selectedCustomer.hospital}</p>
                        )}
                        {selectedCustomer?.location && (
                          <p className="text-sm text-gray-500 flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {selectedCustomer.location}
                          </p>
                        )}
                        {selectedCustomer?.npi && (
                          <p className="text-xs text-gray-400 font-mono">NPI: {selectedCustomer.npi}</p>
                        )}
                      </div>
                    </div>

                    {/* Email Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Provider's Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="email"
                          value={externalProviderEmail}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          placeholder="doctor@hospital.com"
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            emailValidationError ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                      </div>
                      {emailValidationError && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {emailValidationError}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        We'll send them a professional invitation email with your information.
                      </p>
                    </div>
                  </div>

                  {/* What happens next */}
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">What happens next?</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li className="flex items-start">
                        <span className="mr-2">1.</span>
                        <span>The provider receives an email invitation to join RepConnect</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">2.</span>
                        <span>Once they sign up, you can schedule meetings with them</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">3.</span>
                        <span>You'll be notified when they join the platform</span>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                /* Registered Provider: Show location selector */
                <LocationSelector
                  customerId={selectedCustomer?.id}
                  selectedLocation={bookingData.location}
                  onSelectLocation={(location) => {
                    setBookingData({...bookingData, location});
                  }}
                  onAddNewLocation={() => setShowAddLocationModal(true)}
                />
              )}
            </div>
          )}

          {/* Step 2: Date & Time Selection OR External Provider Sent Confirmation */}
          {step === 2 && (
            <div>
              {isExternalProvider ? (
                /* External Provider: Invitation Sent Confirmation */
                <div className="max-w-lg mx-auto text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Invitation Sent!</h2>
                  <p className="text-gray-600 mb-6">
                    We've sent an invitation to <span className="font-semibold">{selectedCustomer?.name}</span> at{' '}
                    <span className="font-semibold text-blue-600">{externalProviderEmail}</span>
                  </p>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-left mb-6">
                    <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-600">1</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          The provider receives your invitation email with information about RepConnect
                        </p>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-600">2</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          They can sign up for a free account and set up their profile
                        </p>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-600">3</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Once they join, you can schedule lunch meetings with them
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500">
                    You can find this provider in your search results once they join RepConnect.
                  </p>
                </div>
              ) : (
                /* Regular flow: Date & Time Selection */
                <SmartCalendar
                  customerId={selectedCustomer?.id}
                  locationId={bookingData.location?.location_id}
                  selectedDate={bookingData.date}
                  selectedTime={bookingData.time}
                  salesRepId={currentUser?.id}
                  onDateTimeSelect={(date, time) => {
                    setBookingData({...bookingData, date, time});
                  }}
                />
              )}
            </div>
          )}

          {/* Step 3: Attendees - Physicians and Staff */}
          {step === 3 && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold mb-4">Select Attendees</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Choose physicians and staff members who will attend this lunch meeting.
                </p>

                {/* Physicians Section - Quick select from location */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-700 mb-4">Physicians at this Location</h4>

                  {loadingLocationPhysicians ? (
                    <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Loading physicians...</p>
                      </div>
                    </div>
                  ) : physiciansAtLocation.length > 0 ? (
                    <div className="space-y-2">
                      {physiciansAtLocation.map((physicianData) => {
                        const physicianId = physicianData.customer_id;
                        const isSelected = bookingData.invitedPhysicians.includes(physicianId);
                        const profile = physicianData.user_profiles;

                        return (
                          <div
                            key={physicianId}
                            onClick={() => handleToggleLocationPhysician(physicianData)}
                            className={`
                              p-3 border rounded-lg cursor-pointer transition-all
                              ${isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                              }
                            `}
                          >
                            <div className="flex items-center">
                              {/* Checkbox */}
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleLocationPhysician(physicianData)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                              />

                              {/* Physician details */}
                              <div className="ml-3 flex-1">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h5 className="text-sm font-medium text-gray-900">
                                      {profile.first_name} {profile.last_name}
                                    </h5>
                                    {profile.specialty && (
                                      <p className="text-xs text-gray-600 mt-1">
                                        {profile.specialty}
                                      </p>
                                    )}
                                  </div>
                                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                                    RepConnect
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">No other physicians found at this location</p>
                      <p className="text-xs text-gray-500 mt-1">Use the search below to add physicians from other locations</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Button to add physicians not at this location */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-medium text-gray-700">Add Other Physicians</h4>
                  <button
                    onClick={() => setShowPhysicianSearch(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Physician</span>
                  </button>
                </div>

                {/* List of Additional Physicians - Show from invitedPhysicians and selectedPhysicianData */}
                {bookingData.invitedPhysicians?.length > 0 ? (
                  <div className="space-y-3">
                    {bookingData.invitedPhysicians.map((physicianId) => {
                      const physician = selectedPhysicianData[physicianId];
                      if (!physician) return null; // Skip if data not yet loaded

                      return (
                        <div key={physicianId} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                                {physician.name.split(' ').map(n => n[0]).join('')}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{physician.name}</p>
                                <p className="text-sm text-gray-600">{physician.specialty}</p>
                                {physician.hospital && (
                                  <p className="text-xs text-gray-500">{physician.hospital}</p>
                                )}
                                {physician.address && (
                                  <p className="text-xs text-gray-400 mt-1">{physician.address}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {physician.isRegistered ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                  RepConnect
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                                  NPI Directory
                                </span>
                              )}
                              <button
                                onClick={() => {
                                  // Remove from invitedPhysicians
                                  setBookingData({
                                    ...bookingData,
                                    invitedPhysicians: bookingData.invitedPhysicians.filter(id => id !== physicianId)
                                  });
                                  // Remove from selectedPhysicianData
                                  setSelectedPhysicianData(prev => {
                                    const newData = {...prev};
                                    delete newData[physicianId];
                                    return newData;
                                  });
                                }}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove physician"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">No additional physicians added</p>
                  </div>
                )}
              </div>

              {/* Staff Selection Section */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-700 mb-4">Staff Members (Optional)</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Select which staff members from {selectedCustomer.name}'s practice will attend.
                </p>

                {availableStaff.length === 0 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-blue-800">No Staff Members Available</p>
                        <p className="text-sm text-blue-700 mt-1">
                          {selectedCustomer.name} hasn't added any staff members yet. You can proceed with just physicians.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Select All Checkbox */}
                    <div
                      onClick={handleSelectAllStaff}
                      className="p-3 rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors flex items-center space-x-3"
                    >
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        availableStaff.length > 0 && availableStaff.every(staff => bookingData.selectedStaff.find(s => s.id === staff.id))
                          ? 'border-blue-600 bg-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {availableStaff.length > 0 && availableStaff.every(staff => bookingData.selectedStaff.find(s => s.id === staff.id)) && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="font-medium text-gray-700">Select All ({availableStaff.length} staff members)</span>
                    </div>

                    {availableStaff.map(staff => {
                      const isSelected = bookingData.selectedStaff.find(s => s.id === staff.id);
                      return (
                        <div
                          key={staff.id}
                          onClick={() => handleStaffSelection(staff)}
                          className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-600'
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-800">{staff.name}</h4>
                                <p className="text-sm text-gray-600">{staff.role}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              {staff.preferences?.dietaryRestrictions?.length > 0 && (
                                <p className="text-xs text-gray-500">
                                  Dietary: {staff.preferences.dietaryRestrictions.slice(0, 2).join(', ')}
                                  {staff.preferences.dietaryRestrictions.length > 2 && '...'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Meeting Summary */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Total Attendees</span>
                </div>
                <p className="text-sm text-blue-700">
                  <span className="font-medium">{getTotalPhysicians()} physician{getTotalPhysicians() !== 1 ? 's' : ''}</span>
                  {bookingData.selectedStaff.length > 0 && (
                    <span> + {bookingData.selectedStaff.length} staff member{bookingData.selectedStaff.length !== 1 ? 's' : ''}</span>
                  )}
                  <span className="ml-2">= {getTotalPhysicians() + bookingData.selectedStaff.length} total</span>
                </p>
              </div>

              {/* Physician Search Modal - Now uses PhysicianSelector component */}
              {showPhysicianSearch && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-gray-200 flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Add Physicians to Meeting</h3>
                        <button
                          onClick={() => {
                            setShowPhysicianSearch(false);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Search RepConnect database and NPI Registry to add physicians
                      </p>
                    </div>

                    <div className="p-4 flex-1 overflow-y-auto">
                      {/* Use PhysicianSelector component for unified search */}
                      <PhysicianSelector
                        locationId={bookingData.location?.location_id}
                        selectedDate={bookingData.date}
                        selectedPhysicians={bookingData.invitedPhysicians}
                        onPhysiciansChange={(physicians) => {
                          setBookingData({...bookingData, invitedPhysicians: physicians});
                        }}
                        onPhysicianDataChange={(physicianId, physicianData, isAdded) => {
                          if (isAdded) {
                            // Add physician data to map
                            setSelectedPhysicianData(prev => ({
                              ...prev,
                              [physicianId]: physicianData
                            }));
                          } else {
                            // Remove physician data from map
                            setSelectedPhysicianData(prev => {
                              const newData = {...prev};
                              delete newData[physicianId];
                              return newData;
                            });
                          }
                        }}
                        organizerId={selectedCustomer?.id}
                      />
                    </div>

                    <div className="p-4 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          <p className="font-medium">
                            {1 + (bookingData.invitedPhysicians?.length || 0)} total physician{1 + (bookingData.invitedPhysicians?.length || 0) !== 1 ? 's' : ''}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            1 organizer {bookingData.invitedPhysicians?.length > 0 && `+ ${bookingData.invitedPhysicians.length} invited`}
                          </p>
                        </div>
                        <button
                          onClick={() => setShowPhysicianSearch(false)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Restaurant Selection */}
          {step === 4 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Select Restaurant</h3>
              
              {/* Restaurant Search and Add Controls */}
              <div className="mb-6 space-y-4">
                <div className="flex flex-wrap gap-3">
                  {/* Search Button */}
                  <button
                    onClick={() => setShowRestaurantSearch(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Search className="w-4 h-4" />
                    <span>Search Restaurants</span>
                  </button>

                  {/* Add Custom Restaurant Button */}
                  <button
                    onClick={() => setShowAddRestaurant(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Custom</span>
                  </button>
                </div>

                {/* Selected Restaurant Display */}
                {bookingData.restaurant && (
                  <div className="p-4 rounded-lg border border-green-500 bg-green-50">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div className="ml-3 flex-1">
                        <h4 className="font-semibold text-green-900">{bookingData.restaurant.name}</h4>
                        <p className="text-sm text-green-700 mt-1">
                          {bookingData.restaurant.address}
                        </p>
                        <p className="text-sm text-green-600 mt-1">
                          {bookingData.restaurant.cuisineType} • {bookingData.restaurant.priceRange}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Smart Recommendations - Top 5 */}
              <div className="mb-8 space-y-3">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  🎯 Top Recommendations for {selectedCustomer.name}
                </h4>
                {sortedRestaurants.map(restaurant => {
                  const isSelected = bookingData.restaurant?.id === restaurant.id;
                  
                  return (
                    <div
                      key={restaurant.id}
                      onClick={() => setBookingData({...bookingData, restaurant})}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors relative ${
                        isSelected
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {/* Intelligence Badges */}
                      {restaurant.isRecommended && (
                        <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                          Recommended
                        </div>
                      )}
                      
                      {restaurant.visitHistory > 0 && (
                        <div className="absolute -top-2 -left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                          {restaurant.visitHistory}x visited
                        </div>
                      )}
                      
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold">{restaurant.name}</h4>
                            {restaurant.isRecommended && (
                              <Lightbulb className="w-4 h-4 text-amber-500" />
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600">
                            {restaurant.cuisine} • {restaurant.avgPrice} • {restaurant.deliveryTime}
                          </p>
                          
                          {restaurant.address && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {restaurant.address}
                            </p>
                          )}

                          {restaurant.popularItems && restaurant.popularItems.length > 0 && (
                            <p className="text-sm text-gray-600 mt-1">
                              Popular: {restaurant.popularItems.join(', ')}
                            </p>
                          )}

                          {/* Intelligence Insights */}
                          {restaurant.reasonsForRecommendation && restaurant.reasonsForRecommendation.length > 0 && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                              <p className="font-medium text-gray-700 mb-1">
                                💡 Why this restaurant (Based on SSi - Smart Selection Intelligence):
                              </p>
                              {restaurant.reasonsForRecommendation.map((reason, idx) => (
                                <p key={idx} className="text-gray-600">• {reason}</p>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center ml-4">
                          {restaurant.rating > 0 && (
                            <>
                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                              <span className="ml-1 text-sm font-semibold">{restaurant.rating}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {sortedRestaurants.length === 0 && (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 mb-2">No recommendations available</p>
                    <p className="text-sm text-gray-500">Use search or add custom restaurants below</p>
                  </div>
                )}
              </div>

              {/* Customer's Favorite Restaurants */}
              {customerFavoriteRestaurants.length > 0 && (
                <div className="mb-8 space-y-3">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    ⭐ {selectedCustomer.name}'s Favorite Restaurants
                  </h4>
                  {customerFavoriteRestaurants.map(restaurant => {
                    const isSelected = bookingData.restaurant?.id === restaurant.id;

                    return (
                      <div
                        key={restaurant.id}
                        onClick={() => setBookingData({...bookingData, restaurant})}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors relative ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {/* Favorite Badge */}
                        <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" />
                          Favorite
                        </div>

                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-semibold">{restaurant.name}</h4>
                            </div>

                            <p className="text-sm text-gray-600">
                              {restaurant.cuisine} • {restaurant.avgPrice}
                              {restaurant.lastBookedDate && (
                                <span className="ml-2 text-blue-600 font-medium">
                                  • Last ordered: {new Date(restaurant.lastBookedDate).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              )}
                            </p>

                            {restaurant.address && (
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {restaurant.address}
                              </p>
                            )}

                            {/* Notes if any */}
                            {restaurant.notes && (
                              <p className="text-xs text-gray-600 mt-2 italic">
                                Note: {restaurant.notes}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center ml-4">
                            {restaurant.rating > 0 && (
                              <>
                                <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                <span className="ml-1 text-sm font-semibold">{restaurant.rating}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Additional Restaurants - Custom and Search Results */}
              {additionalRestaurants.length > 0 && (
                <div className="mt-8 space-y-3">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    📍 Additional Options
                  </h4>
                  {additionalRestaurants.map(restaurant => {
                    const isSelected = bookingData.restaurant?.id === restaurant.id;
                    
                    return (
                      <div
                        key={restaurant.id}
                        onClick={() => setBookingData({...bookingData, restaurant})}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors relative ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-semibold">{restaurant.name}</h4>
                              {restaurant.source === 'custom' && (
                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                  Custom
                                </span>
                              )}
                              {restaurant.source === 'search' && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                  Search Result
                                </span>
                              )}
                            </div>
                            
                            <p className="text-sm text-gray-600">
                              {restaurant.cuisine} • {restaurant.avgPrice} • {restaurant.deliveryTime}
                            </p>
                            
                            {restaurant.address && (
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {restaurant.address}
                              </p>
                            )}

                            {restaurant.popularItems && restaurant.popularItems.length > 0 && (
                              <p className="text-sm text-gray-600 mt-1">
                                Popular: {restaurant.popularItems.join(', ')}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex items-center ml-4">
                            {restaurant.rating > 0 && (
                              <>
                                <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                <span className="ml-1 text-sm font-semibold">{restaurant.rating}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Enhanced Restaurant Search Modal with Google Places */}
              {showRestaurantSearch && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Find Restaurants</h3>
                        <button
                          onClick={() => {
                            setShowRestaurantSearch(false);
                            setRestaurantSearchQuery('');
                            setSearchedRestaurants([]);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                      <YelpRestaurantSearch
                        onRestaurantSelect={handleGoogleRestaurantSelect}
                        selectedLocation={(() => {
                          // Get location directly from user_profiles (city/state fields)
                          const location =
                            selectedCustomer?.city && selectedCustomer?.state
                              ? `${selectedCustomer.city}, ${selectedCustomer.state}`
                              : selectedCustomer?.address ||
                                '';

                          logger.debug('Customer location for Yelp search', { location, customerData: {
                            city: selectedCustomer?.city,
                            state: selectedCustomer?.state,
                            address: selectedCustomer?.address,
                            zip_code: selectedCustomer?.zip_code
                          }});
                          return location;
                        })()}
                        placeholder="Search restaurants near provider location..."
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Add Custom Restaurant Modal */}
              {showAddRestaurant && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Add Custom Restaurant</h3>
                        <button
                          onClick={() => setShowAddRestaurant(false)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Name *</label>
                        <input
                          type="text"
                          value={newRestaurant.name}
                          onChange={(e) => setNewRestaurant(prev => ({...prev, name: e.target.value}))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter restaurant name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cuisine Type</label>
                        <input
                          type="text"
                          value={newRestaurant.cuisine}
                          onChange={(e) => setNewRestaurant(prev => ({...prev, cuisine: e.target.value}))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g. Italian, Mexican, Asian"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <input
                          type="text"
                          value={newRestaurant.address}
                          onChange={(e) => setNewRestaurant(prev => ({...prev, address: e.target.value}))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Restaurant address"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Price Range</label>
                          <select
                            value={newRestaurant.avgPrice}
                            onChange={(e) => setNewRestaurant(prev => ({...prev, avgPrice: e.target.value}))}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="$5-10">$5-10</option>
                            <option value="$10-15">$10-15</option>
                            <option value="$15-20">$15-20</option>
                            <option value="$20-25">$20-25</option>
                            <option value="$25+">$25+</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Time</label>
                          <select
                            value={newRestaurant.deliveryTime}
                            onChange={(e) => setNewRestaurant(prev => ({...prev, deliveryTime: e.target.value}))}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="15-25 min">15-25 min</option>
                            <option value="25-35 min">25-35 min</option>
                            <option value="30-45 min">30-45 min</option>
                            <option value="45-60 min">45-60 min</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="flex space-x-3 pt-4">
                        <button
                          onClick={addCustomRestaurant}
                          disabled={!newRestaurant.name.trim()}
                          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 rounded-lg transition-colors"
                        >
                          Add Restaurant
                        </button>
                        <button
                          onClick={() => setShowAddRestaurant(false)}
                          className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {bookingInsights && bookingInsights.summary.totalMeetings > 0 && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-blue-900 mb-2">Intelligent Recommendations</h4>
                      <p className="text-sm text-blue-800 mb-2">
                        Based on {bookingInsights.summary.totalMeetings} previous meeting{bookingInsights.summary.totalMeetings !== 1 ? 's' : ''} with {selectedCustomer.name}
                      </p>
                      
                      {/* Quick Stats */}
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="bg-white rounded p-3">
                          <div className="flex items-center space-x-2">
                            <TrendingUp className="w-4 h-4 text-green-600" />
                            <span className="text-xs text-gray-600">Average Spend</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            ${bookingInsights.summary.averageSpend.toFixed(2)}
                          </p>
                        </div>
                        <div className="bg-white rounded p-3">
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <span className="text-xs text-gray-600">Prefers</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {bookingInsights.summary.preferredTime || 'Any time'}
                          </p>
                        </div>
                      </div>

                      {/* Quick Reorder Option */}
                      {reorderSuggestion && (
                        <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <RotateCcw className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-medium text-gray-900">Quick Reorder</span>
                              </div>
                              <p className="text-xs text-gray-600">
                                {reorderSuggestion.restaurant} • ${reorderSuggestion.totalCost} • {reorderSuggestion.note}
                              </p>
                            </div>
                            <button
                              onClick={handleQuickReorder}
                              className="px-3 py-1 bg-green-600 text-white text-xs rounded-full hover:bg-green-700 transition-colors"
                            >
                              Reorder
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Basic Provider Info */}
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Preferred restaurants: {selectedCustomer.favoriteRestaurants?.length > 0 ? selectedCustomer.favoriteRestaurants.join(', ') : 'None specified'}
                </p>
                <p className="text-sm text-gray-600">
                  Dietary restrictions: {selectedCustomer.dietaryRestrictions?.length > 0 ? selectedCustomer.dietaryRestrictions.join(', ') : 'None specified'}
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Meeting Notes */}
          {step === 5 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Meeting Notes</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting Notes (Optional)
                  </label>
                  <textarea
                    placeholder="Any special instructions or topics to discuss..."
                    value={bookingData.notes}
                    onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base touch-manipulation"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Confirmation/Overview */}
          {step === 6 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Confirm Meeting Details</h3>
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Provider</p>
                    <p className="font-semibold">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Meeting Location</p>
                    <p className="font-semibold">{bookingData.location?.practice_locations?.location_name || 'Not specified'}</p>
                    {bookingData.location?.practice_locations?.address && (
                      <p className="text-xs text-gray-500 mt-1">{bookingData.location.practice_locations.address}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date & Time</p>
                    <p className="font-semibold">{bookingData.date} at {bookingData.time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Restaurant</p>
                    <p className="font-semibold">{bookingData.restaurant?.name}</p>
                  </div>
                </div>

                {bookingData.notes && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">Notes</p>
                    <p className="font-semibold">{bookingData.notes}</p>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Attendees</p>
                  <p className="font-semibold">
                    {getTotalPhysicians()} Physician{getTotalPhysicians() !== 1 ? 's' : ''}
                    {bookingData.selectedStaff.length > 0 && ` + ${bookingData.selectedStaff.length} Staff`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Phase 3: Step 7/8 - Review & Submit (was Step 6/7) */}
          {((step === 7 && !isExternalProvider) || (step === 8 && isExternalProvider)) && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                {isExternalProvider ? 'Send Invitation' : 'Confirm Meeting Details'}
              </h3>
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Provider</p>
                    <p className="font-semibold">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Meeting Location</p>
                    <p className="font-semibold">{bookingData.location?.practice_locations?.location_name || 'Not specified'}</p>
                    {bookingData.location?.practice_locations?.address && (
                      <p className="text-xs text-gray-500 mt-1">{bookingData.location.practice_locations.address}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date & Time</p>
                    <p className="font-semibold">{bookingData.date} at {bookingData.time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Restaurant</p>
                    <p className="font-semibold">{bookingData.restaurant?.name}</p>
                  </div>
                </div>
                
                {/* External Provider Email Info */}
                {isExternalProvider && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <Mail className="w-4 h-4 text-blue-600" />
                        <p className="text-sm font-medium text-blue-900">Invitation will be sent to:</p>
                      </div>
                      <p className="text-sm font-semibold text-blue-800">{externalProviderEmail}</p>
                      <p className="text-xs text-blue-700 mt-1">
                        The provider will receive a professional email with meeting details and confirmation options.
                      </p>
                    </div>
                  </div>
                )}

                {bookingData.notes && (
                  <div>
                    <p className="text-sm text-gray-600">Notes</p>
                    <p className="font-semibold">{bookingData.notes}</p>
                  </div>
                )}
                
                {/* Staff Summary */}
                {bookingData.selectedStaff.length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-600">Attendees</p>
                    <div className="mt-2 space-y-1">
                      <p className="font-medium text-gray-800">{selectedCustomer.name} (Physician)</p>
                      {bookingData.selectedStaff.map(staff => (
                        <p key={staff.id} className="text-sm text-gray-600">• {staff.name} ({staff.role})</p>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Estimated total cost for compliance</p>
                      <p className="text-xs text-gray-500">
                        {bookingData.selectedStaff.length + 1} attendee{bookingData.selectedStaff.length !== 0 ? 's' : ''}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">
                      ${((bookingData.selectedStaff.length + 1) * 50).toFixed(0)}-{((bookingData.selectedStaff.length + 1) * 65).toFixed(0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="p-4 sm:p-6 border-t flex flex-col sm:flex-row gap-3 sm:justify-between flex-shrink-0 bg-white">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : setCurrentView('find')}
            className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
          >
            {step === 1 ? 'Cancel' : 'Previous'}
          </button>
          <button
            onClick={async () => {
              // External provider: Simple 2-step invitation flow
              if (isExternalProvider) {
                if (step === 1) {
                  // Send invitation email and track referral
                  try {
                    setIsSubmitting(true);

                    const salesRepName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Your RepConnect contact';

                    // Send the invitation email
                    await emailService.sendExternalProviderInvite({
                      providerName: selectedCustomer?.name,
                      providerEmail: externalProviderEmail,
                      providerNpi: selectedCustomer?.npi,
                      providerSpecialty: selectedCustomer?.specialty,
                      salesRepName: salesRepName,
                      salesRepEmail: currentUser?.email,
                      salesRepCompany: currentUser?.company || ''
                    });

                    // Track the referral for commission/rewards (don't block on failure)
                    try {
                      await referralService.createReferral({
                        referrerId: currentUser?.id,
                        referrerEmail: currentUser?.email,
                        referrerName: salesRepName,
                        inviteeEmail: externalProviderEmail,
                        inviteeName: selectedCustomer?.name,
                        inviteeNpi: selectedCustomer?.npi,
                        inviteeSpecialty: selectedCustomer?.specialty,
                        inviteeHospital: selectedCustomer?.hospital
                      });
                      logger.debug('Referral tracked successfully');
                    } catch (referralError) {
                      // Log but don't block - referral tracking is non-critical
                      logger.error('Failed to track referral', { error: referralError.message });
                    }

                    setInvitationSent(true);
                    setStep(2);
                  } catch (error) {
                    logger.error('Failed to send invitation', { error: error.message });
                    alert(`Failed to send invitation: ${error.message}`);
                  } finally {
                    setIsSubmitting(false);
                  }
                } else {
                  // Step 2 (Sent) - go back to find providers
                  setCurrentView('find');
                }
              } else {
                // Normal flow: 6 steps total
                const maxStep = 6;
                if (step < maxStep) {
                  setStep(step + 1);
                } else {
                  handleConfirmBooking();
                }
              }
            }}
            disabled={isSubmitting ||
              (step === 1 && isExternalProvider && (!externalProviderEmail || emailValidationError)) ||
              (step === 1 && !isExternalProvider && !bookingData.location) ||
              (step === 2 && !isExternalProvider && (!bookingData.date || !bookingData.time)) ||
              (step === 4 && !isExternalProvider && !bookingData.restaurant)
            }
            className={`w-full sm:w-auto px-6 py-3 rounded-lg transition-colors touch-manipulation ${
              isSubmitting ||
              (step === 1 && isExternalProvider && (!externalProviderEmail || emailValidationError)) ||
              (step === 1 && !isExternalProvider && !bookingData.location) ||
              (step === 2 && !isExternalProvider && (!bookingData.date || !bookingData.time)) ||
              (step === 4 && !isExternalProvider && !bookingData.restaurant)
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white'
            }`}
          >
            {isSubmitting ?
              'Sending...' :
              isExternalProvider ?
                (step === 1 ? 'Send Invitation' : 'Done') :
                (step === 6 ? 'Confirm Booking' : 'Next')
            }
          </button>
        </div>
      </div>

      {/* Phase 3: Add Location Modal */}
      <AddLocationModal
        customerId={selectedCustomer?.id}
        isOpen={showAddLocationModal}
        onClose={() => setShowAddLocationModal(false)}
        onLocationAdded={(location) => {
          setBookingData({...bookingData, location});
          setShowAddLocationModal(false);
          // Auto-advance to step 2 after adding location
          setStep(2);
        }}
      />

      {/* Time Slot Selection Modal */}
      {showTimeSlotModal && selectedCalendarDate && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Select Time Slot
              </h3>
              <button
                onClick={() => {
                  setShowTimeSlotModal(false);
                  setSelectedCalendarDate(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {new Date(selectedCalendarDate + 'T12:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {(() => {
              const slots = getAvailableTimeSlotsForDay(selectedCalendarDate);

              if (slots.length === 0) {
                return (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No available time slots for this day</p>
                    <p className="text-sm text-gray-500 mt-2">All slots are currently booked</p>
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  <p className="text-sm text-gray-700 mb-4">
                    {slots.length} available time slot{slots.length !== 1 ? 's' : ''}
                  </p>
                  {slots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() => handleTimeSlotSelection(slot)}
                      className="w-full p-4 border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 group-hover:text-blue-700">
                            {slot.timeRange}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {slot.label}
                          </div>
                        </div>
                        <div className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default React.memo(BookMeetingView);